import React, {
  useEffect,
  useState,
  useCallback,
  useRef,
  useMemo,
} from "react";
import {
  Card,
  Col,
  Row,
  Table,
  Pagination,
  Badge,
  Spinner,
  Dropdown,
  ButtonGroup,
} from "react-bootstrap";
import {
  TrendingUp,
  TrendingDown,
  AlertTriangle,
  CheckCircle,
  Activity,
  Wifi,
  Zap,
  Server,
  Globe,
  Shield,
  Smartphone,
  Tv,
  Thermometer,
  Play,
  RefreshCw,
  AlertOctagon,
  Users,
  Cpu,
  Droplet,
  Percent,
  Target,
  Search,
  Eye,
  EyeOff,
  Boxes,
  Orbit,
  Signal,
  MapPin,
  Database,
  Network,
  Radio,
  Focus,
  Maximize2,
  Minimize2,
  Laptop,
  Camera,
  Cloud,
  Router,
  X,
  Filter,
  Layers,
  Plus,
  Minus,
  Home,
  Info,
  Link,
  HardDrive,
} from "lucide-react";

// ==================== TYPES ====================
interface NetworkNode {
  id: string;
  label: string;
  type: string;
  status: "critical" | "warning" | "normal";
  cause?: string;
  fix?: string;
  x: number;
  y: number;
  vx?: number;
  vy?: number;
  fx?: number | null;
  fy?: number | null;
  threatLevel?: "high" | "medium" | "low" | "none";
  threatType?: string;
  metadata?: {
    ip?: string;
    uptime?: string;
    bandwidth?: string;
    location?: string;
    deviceType?: string;
    os?: string;
    lastSeen?: string;
  };
}

interface DashboardStats {
  subscribersAffected: number;
  devices: number;
  hourlyLoss: number;
  avgLatency: number;
  northLatency: number;
  throughput: number;
  throughputDelta: number;
  activeThreats: number;
  threatsDelta: number;
  packetLoss: number;
  packetLossDelta: number;
  networkUptime: number;
  totalBandwidth: number;
  activeAlerts: number;
}

interface AlertItem {
  id: string;
  severity: "critical" | "warning" | "info";
  message: string;
  timestamp: Date;
  source: string;
}

type ViewLevel = "network" | "device" | "event";

// ==================== STATIC DATA (NETWORK TOPOLOGY) ====================
const generateNetworkNodes = (): NetworkNode[] => {
  const devices: NetworkNode[] = [];

  // Core infrastructure devices
  const coreDevices = [
    {
      id: "core-router-01",
      label: "Core Router DC1",
      type: "router",
      status: "normal" as const,
      x: 500,
      y: 300,
      metadata: {
        ip: "10.0.0.1",
        uptime: "99.99%",
        bandwidth: "100Gbps",
        location: "Data Center 1",
        deviceType: "Core Router",
      },
    },
    {
      id: "core-switch-01",
      label: "Core Switch DC1",
      type: "switch",
      status: "normal" as const,
      x: 650,
      y: 350,
      metadata: {
        ip: "10.0.0.2",
        uptime: "99.99%",
        bandwidth: "100Gbps",
        location: "Data Center 1",
        deviceType: "Core Switch",
      },
    },
    {
      id: "firewall-01",
      label: "Firewall Cluster",
      type: "firewall",
      status: "normal" as const,
      x: 400,
      y: 250,
      metadata: {
        ip: "10.0.0.3",
        uptime: "99.99%",
        bandwidth: "40Gbps",
        location: "Data Center 1",
        deviceType: "Firewall",
      },
    },
    {
      id: "load-balancer-01",
      label: "Load Balancer",
      type: "loadbalancer",
      status: "normal" as const,
      x: 550,
      y: 400,
      metadata: {
        ip: "10.0.0.4",
        uptime: "99.95%",
        bandwidth: "80Gbps",
        location: "Data Center 1",
        deviceType: "Load Balancer",
      },
    },
  ];

  // Edge routers with threats
  const edgeRouters = [
    {
      id: "edge-nyc-01",
      label: "Edge Router NYC",
      type: "router",
      status: "critical" as const,
      cause: "DDoS attack detected. 5.2Gbps UDP flood from botnet",
      fix: "Activate DDoS mitigation. Blacklist malicious IPs. Enable rate limiting.",
      x: 300,
      y: 200,
      threatLevel: "high" as const,
      threatType: "DDoS Attack",
      metadata: {
        ip: "203.0.113.1",
        uptime: "98.5%",
        bandwidth: "10Gbps",
        location: "New York",
        deviceType: "Edge Router",
      },
    },
    {
      id: "edge-lon-01",
      label: "Edge Router London",
      type: "router",
      status: "warning" as const,
      cause: "Port scan detected from 185.220.101.47",
      fix: "Block scanning IP. Update ACL rules.",
      x: 700,
      y: 200,
      threatLevel: "medium" as const,
      threatType: "Port Scanning",
      metadata: {
        ip: "203.0.113.2",
        uptime: "99.2%",
        bandwidth: "10Gbps",
        location: "London",
        deviceType: "Edge Router",
      },
    },
    {
      id: "edge-tok-01",
      label: "Edge Router Tokyo",
      type: "router",
      status: "warning" as const,
      cause: "Unusual traffic pattern detected",
      fix: "Analyze traffic. Enable anomaly detection.",
      x: 800,
      y: 500,
      threatLevel: "low" as const,
      threatType: "Anomaly",
      metadata: {
        ip: "203.0.113.3",
        uptime: "99.7%",
        bandwidth: "10Gbps",
        location: "Tokyo",
        deviceType: "Edge Router",
      },
    },
    {
      id: "edge-syd-01",
      label: "Edge Router Sydney",
      type: "router",
      status: "normal" as const,
      x: 200,
      y: 600,
      metadata: {
        ip: "203.0.113.4",
        uptime: "99.9%",
        bandwidth: "10Gbps",
        location: "Sydney",
        deviceType: "Edge Router",
      },
    },
  ];

  // 5G Towers with threats
  const towers = [
    {
      id: "5g-tower-01",
      label: "5G Tower Downtown",
      type: "5g",
      status: "critical" as const,
      cause: "Backhaul congestion. Packet loss 5.2%",
      fix: "Reroute traffic. Increase bandwidth allocation.",
      x: 350,
      y: 150,
      threatLevel: "high" as const,
      metadata: {
        ip: "10.1.1.1",
        uptime: "97.8%",
        bandwidth: "5Gbps",
        location: "Downtown",
        deviceType: "5G Tower",
      },
    },
    {
      id: "5g-tower-02",
      label: "5G Tower Suburb",
      type: "5g",
      status: "warning" as const,
      cause: "High utilization 85%",
      fix: "Add capacity. Optimize beamforming.",
      x: 450,
      y: 100,
      threatLevel: "medium" as const,
      metadata: {
        ip: "10.1.1.2",
        uptime: "99.1%",
        bandwidth: "5Gbps",
        location: "Suburb",
        deviceType: "5G Tower",
      },
    },
    {
      id: "5g-tower-03",
      label: "5G Tower Industrial",
      type: "5g",
      status: "normal" as const,
      x: 250,
      y: 300,
      metadata: {
        ip: "10.1.1.3",
        uptime: "99.8%",
        bandwidth: "5Gbps",
        location: "Industrial Zone",
        deviceType: "5G Tower",
      },
    },
    {
      id: "5g-tower-04",
      label: "5G Tower Airport",
      type: "5g",
      status: "warning" as const,
      cause: "Interference detected",
      fix: "Adjust frequency. Run spectrum analysis.",
      x: 600,
      y: 100,
      threatLevel: "low" as const,
      metadata: {
        ip: "10.1.1.4",
        uptime: "99.3%",
        bandwidth: "5Gbps",
        location: "Airport",
        deviceType: "5G Tower",
      },
    },
  ];

  // Botnet infected devices (threats)
  const botnetDevices = [
    {
      id: "bot-camera-01",
      label: "Bot: Security Camera",
      type: "bot",
      status: "critical" as const,
      cause: "Mirai botnet. Sending UDP floods",
      fix: "Quarantine device. Push firmware update.",
      x: 150,
      y: 400,
      threatLevel: "high" as const,
      threatType: "Mirai Botnet",
      metadata: {
        ip: "192.168.1.50",
        location: "Customer Site A",
        deviceType: "IoT Camera",
        os: "Linux 2.6",
      },
    },
    {
      id: "bot-camera-02",
      label: "Bot: Doorbell Cam",
      type: "bot",
      status: "critical" as const,
      cause: "Participating in DDoS attack",
      fix: "Isolate from network. Force password reset.",
      x: 100,
      y: 450,
      threatLevel: "high" as const,
      threatType: "DDoS Bot",
      metadata: {
        ip: "192.168.1.51",
        location: "Customer Site B",
        deviceType: "Doorbell",
        os: "RTOS",
      },
    },
    {
      id: "bot-tv-01",
      label: "Bot: Smart TV",
      type: "bot",
      status: "critical" as const,
      cause: "Part of botnet. Sending spam",
      fix: "Block MAC address. Notify customer.",
      x: 200,
      y: 500,
      threatLevel: "high" as const,
      threatType: "Spam Bot",
      metadata: {
        ip: "192.168.1.60",
        location: "Customer Site C",
        deviceType: "Smart TV",
        os: "Android TV",
      },
    },
    {
      id: "bot-tv-02",
      label: "Bot: Smart TV LR",
      type: "bot",
      status: "warning" as const,
      cause: "Suspicious outbound connections",
      fix: "Monitor traffic. Update antivirus.",
      x: 250,
      y: 550,
      threatLevel: "medium" as const,
      threatType: "C2 Communication",
      metadata: {
        ip: "192.168.1.61",
        location: "Customer Site D",
        deviceType: "Smart TV",
        os: "Tizen",
      },
    },
    {
      id: "bot-thermo-01",
      label: "Bot: Smart Thermostat",
      type: "bot",
      status: "critical" as const,
      cause: "Mirai variant detected",
      fix: "Quarantine. Factory reset required.",
      x: 300,
      y: 600,
      threatLevel: "high" as const,
      threatType: "Mirai Variant",
      metadata: {
        ip: "192.168.1.70",
        location: "Customer Site E",
        deviceType: "Thermostat",
        os: "Embedded",
      },
    },
    {
      id: "bot-thermo-02",
      label: "Bot: HVAC Control",
      type: "bot",
      status: "warning" as const,
      cause: "Unusual outbound traffic",
      fix: "Analyze traffic patterns. Apply updates.",
      x: 350,
      y: 650,
      threatLevel: "medium" as const,
      threatType: "Anomaly",
      metadata: {
        ip: "192.168.1.71",
        location: "Customer Site F",
        deviceType: "HVAC",
        os: "Embedded",
      },
    },
    {
      id: "bot-fridge-01",
      label: "Bot: Smart Fridge",
      type: "bot",
      status: "critical" as const,
      cause: "Part of IoT botnet",
      fix: "Disconnect from network. Factory reset.",
      x: 400,
      y: 700,
      threatLevel: "high" as const,
      threatType: "IoT Botnet",
      metadata: {
        ip: "192.168.1.80",
        location: "Customer Site G",
        deviceType: "Smart Fridge",
        os: "Android",
      },
    },
    {
      id: "bot-speaker-01",
      label: "Bot: Smart Speaker",
      type: "bot",
      status: "warning" as const,
      cause: "Suspicious DNS queries",
      fix: "Block malicious domains. Update firmware.",
      x: 450,
      y: 750,
      threatLevel: "medium" as const,
      threatType: "DNS Tunneling",
      metadata: {
        ip: "192.168.1.90",
        location: "Customer Site H",
        deviceType: "Smart Speaker",
        os: "Custom",
      },
    },
    {
      id: "bot-router-01",
      label: "Bot: Home Router",
      type: "bot",
      status: "critical" as const,
      cause: "DNS hijacking detected",
      fix: "Force router reboot. Update firmware.",
      x: 500,
      y: 800,
      threatLevel: "high" as const,
      threatType: "DNS Hijacking",
      metadata: {
        ip: "192.168.1.1",
        location: "Customer Site I",
        deviceType: "Router",
        os: "OpenWRT",
      },
    },
    {
      id: "bot-phone-01",
      label: "Bot: Mobile Device",
      type: "bot",
      status: "warning" as const,
      cause: "Malware detected",
      fix: "Scan device. Remove malicious apps.",
      x: 550,
      y: 850,
      threatLevel: "medium" as const,
      threatType: "Mobile Malware",
      metadata: {
        ip: "10.0.0.100",
        location: "Customer Site J",
        deviceType: "Smartphone",
        os: "Android",
      },
    },
  ];

  // Customer gateways (compromised)
  const gateways = [
    {
      id: "gw-comp-01",
      label: "Gateway: Customer A",
      type: "gateway",
      status: "critical" as const,
      cause: "Gateway compromised. Botnet C2 active",
      fix: "Isolate gateway. Deploy security patch.",
      x: 180,
      y: 350,
      threatLevel: "high" as const,
      metadata: {
        ip: "192.168.1.1",
        location: "Customer Site A",
        deviceType: "Gateway",
      },
    },
    {
      id: "gw-comp-02",
      label: "Gateway: Customer B",
      type: "gateway",
      status: "warning" as const,
      cause: "Unusual outbound traffic",
      fix: "Monitor and analyze. Apply QoS rules.",
      x: 280,
      y: 450,
      threatLevel: "medium" as const,
      metadata: {
        ip: "192.168.2.1",
        location: "Customer Site B",
        deviceType: "Gateway",
      },
    },
    {
      id: "gw-comp-03",
      label: "Gateway: Customer C",
      type: "gateway",
      status: "normal" as const,
      x: 380,
      y: 550,
      metadata: {
        ip: "192.168.3.1",
        location: "Customer Site C",
        deviceType: "Gateway",
      },
    },
    {
      id: "gw-secure-01",
      label: "Gateway: Bank HQ",
      type: "gateway",
      status: "normal" as const,
      x: 650,
      y: 300,
      metadata: {
        ip: "10.50.1.1",
        location: "Bank HQ",
        deviceType: "Secure Gateway",
      },
    },
    {
      id: "gw-secure-02",
      label: "Gateway: Gov Office",
      type: "gateway",
      status: "normal" as const,
      x: 750,
      y: 350,
      metadata: {
        ip: "10.60.1.1",
        location: "Government Office",
        deviceType: "Secure Gateway",
      },
    },
  ];

  // IoT devices (legitimate but monitored)
  const iotDevices = [
    {
      id: "iot-camera-01",
      label: "IP Camera Lobby",
      type: "iot",
      status: "normal" as const,
      x: 150,
      y: 250,
      metadata: {
        ip: "10.10.1.50",
        location: "Main Office",
        deviceType: "IP Camera",
      },
    },
    {
      id: "iot-camera-02",
      label: "IP Camera Parking",
      type: "iot",
      status: "normal" as const,
      x: 200,
      y: 280,
      metadata: {
        ip: "10.10.1.51",
        location: "Parking",
        deviceType: "IP Camera",
      },
    },
    {
      id: "iot-sensor-01",
      label: "Temp Sensor Rack1",
      type: "iot",
      status: "warning" as const,
      cause: "Temperature high",
      fix: "Check cooling system",
      x: 300,
      y: 350,
      metadata: {
        ip: "10.10.2.10",
        location: "Server Room",
        deviceType: "Temperature Sensor",
      },
    },
    {
      id: "iot-sensor-02",
      label: "Temp Sensor Rack2",
      type: "iot",
      status: "normal" as const,
      x: 350,
      y: 380,
      metadata: {
        ip: "10.10.2.11",
        location: "Server Room",
        deviceType: "Temperature Sensor",
      },
    },
    {
      id: "iot-sensor-03",
      label: "Humidity Sensor",
      type: "iot",
      status: "normal" as const,
      x: 400,
      y: 410,
      metadata: {
        ip: "10.10.2.12",
        location: "Server Room",
        deviceType: "Humidity Sensor",
      },
    },
    {
      id: "iot-door-01",
      label: "Door Lock Main",
      type: "iot",
      status: "normal" as const,
      x: 450,
      y: 440,
      metadata: {
        ip: "10.10.3.20",
        location: "Main Entrance",
        deviceType: "Smart Lock",
      },
    },
    {
      id: "iot-door-02",
      label: "Door Lock Back",
      type: "iot",
      status: "normal" as const,
      x: 500,
      y: 470,
      metadata: {
        ip: "10.10.3.21",
        location: "Back Entrance",
        deviceType: "Smart Lock",
      },
    },
    {
      id: "iot-light-01",
      label: "Smart Light A",
      type: "iot",
      status: "normal" as const,
      x: 550,
      y: 500,
      metadata: {
        ip: "10.10.4.30",
        location: "Conference Room",
        deviceType: "Smart Light",
      },
    },
    {
      id: "iot-light-02",
      label: "Smart Light B",
      type: "iot",
      status: "normal" as const,
      x: 600,
      y: 530,
      metadata: {
        ip: "10.10.4.31",
        location: "Office Area",
        deviceType: "Smart Light",
      },
    },
  ];

  // Enterprise servers
  const servers = [
    {
      id: "server-web-01",
      label: "Web Server",
      type: "server",
      status: "critical" as const,
      cause: "Under DDoS attack",
      fix: "Enable WAF. Scale up resources.",
      x: 700,
      y: 450,
      threatLevel: "high" as const,
      metadata: {
        ip: "10.20.1.10",
        uptime: "99.5%",
        location: "Data Center",
        deviceType: "Web Server",
      },
    },
    {
      id: "server-db-01",
      label: "Database Server",
      type: "server",
      status: "normal" as const,
      x: 750,
      y: 500,
      metadata: {
        ip: "10.20.1.20",
        uptime: "99.99%",
        location: "Data Center",
        deviceType: "Database",
      },
    },
    {
      id: "server-mail-01",
      label: "Mail Server",
      type: "server",
      status: "warning" as const,
      cause: "Spam flood detected",
      fix: "Enable spam filtering. Rate limit.",
      x: 800,
      y: 550,
      threatLevel: "medium" as const,
      metadata: {
        ip: "10.20.1.30",
        uptime: "99.2%",
        location: "Data Center",
        deviceType: "Mail Server",
      },
    },
    {
      id: "server-dns-01",
      label: "DNS Server",
      type: "server",
      status: "critical" as const,
      cause: "DNS amplification attack",
      fix: "Rate limit queries. Enable DNSSEC.",
      x: 850,
      y: 600,
      threatLevel: "high" as const,
      metadata: {
        ip: "10.20.1.40",
        uptime: "98.7%",
        location: "Data Center",
        deviceType: "DNS Server",
      },
    },
    {
      id: "server-backup-01",
      label: "Backup Server",
      type: "server",
      status: "normal" as const,
      x: 900,
      y: 650,
      metadata: {
        ip: "10.20.1.50",
        uptime: "99.95%",
        location: "Data Center",
        deviceType: "Backup Server",
      },
    },
    {
      id: "server-mon-01",
      label: "Monitoring Server",
      type: "server",
      status: "normal" as const,
      x: 950,
      y: 700,
      metadata: {
        ip: "10.20.1.60",
        uptime: "99.99%",
        location: "Data Center",
        deviceType: "Monitoring",
      },
    },
  ];

  // Security devices
  const securityDevices = [
    {
      id: "ids-01",
      label: "IDS Sensor",
      type: "security",
      status: "critical" as const,
      cause: "Multiple intrusion attempts detected",
      fix: "Update signatures. Block attacker IPs.",
      x: 500,
      y: 200,
      threatLevel: "high" as const,
      metadata: {
        ip: "10.30.1.10",
        location: "Network Core",
        deviceType: "IDS",
      },
    },
    {
      id: "ips-01",
      label: "IPS Device",
      type: "security",
      status: "normal" as const,
      x: 550,
      y: 250,
      metadata: {
        ip: "10.30.1.20",
        location: "Network Core",
        deviceType: "IPS",
      },
    },
    {
      id: "waf-01",
      label: "WAF Cluster",
      type: "security",
      status: "warning" as const,
      cause: "SQL injection attempts",
      fix: "Update WAF rules. Enable stricter filtering.",
      x: 600,
      y: 300,
      threatLevel: "medium" as const,
      metadata: {
        ip: "10.30.1.30",
        location: "Web Tier",
        deviceType: "WAF",
      },
    },
  ];

  // Workstations
  const workstations = [
    {
      id: "ws-admin-01",
      label: "Admin Workstation",
      type: "workstation",
      status: "normal" as const,
      x: 100,
      y: 150,
      metadata: {
        ip: "10.40.1.10",
        location: "Admin Office",
        deviceType: "Workstation",
      },
    },
    {
      id: "ws-dev-01",
      label: "Dev Workstation",
      type: "workstation",
      status: "normal" as const,
      x: 130,
      y: 180,
      metadata: {
        ip: "10.40.1.20",
        location: "Dev Area",
        deviceType: "Workstation",
      },
    },
    {
      id: "ws-finance-01",
      label: "Finance PC",
      type: "workstation",
      status: "warning" as const,
      cause: "Suspicious process running",
      fix: "Run antivirus scan. Check for malware.",
      x: 160,
      y: 210,
      threatLevel: "medium" as const,
      metadata: {
        ip: "10.40.1.30",
        location: "Finance Dept",
        deviceType: "Workstation",
      },
    },
  ];

  // C&C Servers (malicious external)
  const c2Servers = [
    {
      id: "c2-mirai-01",
      label: "C&C: Mirai Server",
      type: "malicious",
      status: "critical" as const,
      cause: "Command & Control active",
      fix: "Block IP range. Notify CERT.",
      x: 50,
      y: 700,
      threatLevel: "high" as const,
      threatType: "C2 Server",
      metadata: {
        ip: "185.220.101.47",
        location: "Unknown",
        deviceType: "C2 Server",
      },
    },
    {
      id: "c2-apt-01",
      label: "C&C: APT Group",
      type: "malicious",
      status: "critical" as const,
      cause: "APT communication detected",
      fix: "Isolate infected hosts. Threat hunting.",
      x: 80,
      y: 750,
      threatLevel: "high" as const,
      threatType: "APT C2",
      metadata: {
        ip: "45.155.205.33",
        location: "Unknown",
        deviceType: "C2 Server",
      },
    },
    {
      id: "c2-ransom-01",
      label: "C&C: Ransomware",
      type: "malicious",
      status: "warning" as const,
      cause: "Ransomware beaconing",
      fix: "Block domains. Update endpoint protection.",
      x: 110,
      y: 800,
      threatLevel: "high" as const,
      threatType: "Ransomware C2",
      metadata: {
        ip: "94.102.61.78",
        location: "Unknown",
        deviceType: "C2 Server",
      },
    },
  ];

  // Cloud services
  const cloudServices = [
    {
      id: "cloud-aws-01",
      label: "AWS Region",
      type: "cloud",
      status: "normal" as const,
      x: 1000,
      y: 200,
      metadata: {
        ip: "52.0.0.0",
        location: "AWS us-east-1",
        deviceType: "Cloud",
      },
    },
    {
      id: "cloud-azure-01",
      label: "Azure Region",
      type: "cloud",
      status: "normal" as const,
      x: 1050,
      y: 250,
      metadata: {
        ip: "20.0.0.0",
        location: "Azure East US",
        deviceType: "Cloud",
      },
    },
    {
      id: "cloud-gcp-01",
      label: "GCP Region",
      type: "cloud",
      status: "normal" as const,
      x: 1100,
      y: 300,
      metadata: {
        ip: "34.0.0.0",
        location: "GCP us-central1",
        deviceType: "Cloud",
      },
    },
  ];

  return [
    ...coreDevices,
    ...edgeRouters,
    ...towers,
    ...botnetDevices,
    ...gateways,
    ...iotDevices,
    ...servers,
    ...securityDevices,
    ...workstations,
    ...c2Servers,
    ...cloudServices,
  ];
};

const initialNodes = generateNetworkNodes();

// Generate edges between nodes based on logical relationships
const generateEdges = (
  nodes: NetworkNode[]
): { from: NetworkNode; to: NetworkNode; isCritical: boolean }[] => {
  const edges: { from: NetworkNode; to: NetworkNode; isCritical: boolean }[] =
    [];

  // Helper to find node by id
  const findNode = (id: string) => nodes.find((n) => n.id === id)!;

  // Core to edges
  const coreRouter = findNode("core-router-01");
  const edgeRouters = nodes.filter((n) => n.id.startsWith("edge-"));
  edgeRouters.forEach((edge) => {
    if (coreRouter)
      edges.push({
        from: coreRouter,
        to: edge,
        isCritical: edge.status === "critical",
      });
  });

  // Edge routers to 5G towers
  const towers = nodes.filter((n) => n.id.startsWith("5g-tower-"));
  edgeRouters.forEach((edge) => {
    towers.forEach((tower) => {
      edges.push({
        from: edge,
        to: tower,
        isCritical: tower.status === "critical" || edge.status === "critical",
      });
    });
  });

  // Bots to edge routers (critical)
  const bots = nodes.filter((n) => n.type === "bot");
  edgeRouters.forEach((edge) => {
    bots.forEach((bot) => {
      edges.push({ from: bot, to: edge, isCritical: true });
    });
  });

  // Compromised gateways to edge routers
  const compromisedGateways = nodes.filter((n) => n.id.startsWith("gw-comp-"));
  edgeRouters.forEach((edge) => {
    compromisedGateways.forEach((gw) => {
      edges.push({ from: gw, to: edge, isCritical: gw.status === "critical" });
    });
  });

  // C2 to bots
  const c2Servers = nodes.filter((n) => n.id.startsWith("c2-"));
  bots.forEach((bot) => {
    c2Servers.forEach((c2) => {
      edges.push({ from: c2, to: bot, isCritical: true });
    });
  });

  // Core switch to servers
  const coreSwitch = findNode("core-switch-01");
  const servers = nodes.filter((n) => n.type === "server");
  servers.forEach((server) => {
    if (coreSwitch)
      edges.push({
        from: coreSwitch,
        to: server,
        isCritical: server.status === "critical",
      });
  });

  // Security devices to core router
  const securityDevices = nodes.filter((n) => n.type === "security");
  securityDevices.forEach((sec) => {
    if (coreRouter)
      edges.push({
        from: coreRouter,
        to: sec,
        isCritical: sec.status === "critical",
      });
  });

  // Cloud to core router
  const cloudServices = nodes.filter((n) => n.type === "cloud");
  cloudServices.forEach((cloud) => {
    if (coreRouter)
      edges.push({ from: coreRouter, to: cloud, isCritical: false });
  });

  // IoT devices to gateways
  const iotDevices = nodes.filter((n) => n.type === "iot");
  const gateways = nodes.filter((n) => n.type === "gateway");
  gateways.forEach((gw) => {
    iotDevices.forEach((iot) => {
      edges.push({ from: iot, to: gw, isCritical: false });
    });
  });

  return edges;
};

const regions = [
  {
    name: "South Region",
    status: "normal",
    latency: 24,
    jitter: 28,
    packetLoss: 0.01,
    peakLatency: 42,
    subscribers: 1247,
    bandwidth: "2.4Gbps",
  },
  {
    name: "East Region",
    status: "normal",
    latency: 18,
    jitter: 22,
    packetLoss: 0.0,
    peakLatency: 3,
    subscribers: 892,
    bandwidth: "1.8Gbps",
  },
  {
    name: "West Region",
    status: "warning",
    latency: 31,
    jitter: 45,
    packetLoss: 0.12,
    peakLatency: 12,
    subscribers: 654,
    bandwidth: "1.2Gbps",
  },
  {
    name: "North Region",
    status: "critical",
    latency: 340,
    jitter: 120,
    packetLoss: 4.8,
    peakLatency: 340,
    subscribers: 98,
    bandwidth: "0.3Gbps",
  },
];

const stats: DashboardStats = {
  subscribersAffected: 1247,
  devices: 3891,
  hourlyLoss: 47200,
  avgLatency: 103,
  northLatency: 340,
  throughput: 120,
  throughputDelta: -80,
  activeThreats: 7,
  threatsDelta: 7,
  packetLoss: 4.8,
  packetLossDelta: 3.2,
  networkUptime: 99.2,
  totalBandwidth: 1850,
  activeAlerts: 12,
};

const alerts: AlertItem[] = [
  {
    id: "1",
    severity: "critical",
    message: "DDoS attack detected on Edge Router NYC",
    timestamp: new Date(),
    source: "IDS System",
  },
  {
    id: "2",
    severity: "critical",
    message: "Multiple botnet nodes active - Mirai variant",
    timestamp: new Date(Date.now() - 5 * 60000),
    source: "Threat Intel",
  },
  {
    id: "3",
    severity: "warning",
    message: "High packet loss on North 5G tower",
    timestamp: new Date(Date.now() - 10 * 60000),
    source: "Network Monitor",
  },
  {
    id: "4",
    severity: "warning",
    message: "CPU usage above 80% on Core Switch",
    timestamp: new Date(Date.now() - 15 * 60000),
    source: "System Health",
  },
  {
    id: "5",
    severity: "info",
    message: "Backup route activated for East region",
    timestamp: new Date(Date.now() - 30 * 60000),
    source: "Network Controller",
  },
  {
    id: "6",
    severity: "critical",
    message: "C2 communication detected from compromised gateway",
    timestamp: new Date(Date.now() - 2 * 60000),
    source: "Threat Intel",
  },
];

// Helper functions
const getStatusIcon = (status: string) => {
  switch (status) {
    case "critical":
      return <AlertOctagon className="w-4 h-4" />;
    case "warning":
      return <AlertTriangle className="w-4 h-4" />;
    default:
      return <CheckCircle className="w-4 h-4" />;
  }
};

const getNodeIcon = (label: string) => {
  if (label.includes("Router") || label.includes("Edge"))
    return <Router className="w-4 h-4" />;
  if (label.includes("5G") || label.includes("Tower"))
    return <Radio className="w-4 h-4" />;
  if (label.includes("Bot")) {
    if (label.includes("Camera")) return <Camera className="w-4 h-4" />;
    if (label.includes("TV")) return <Tv className="w-4 h-4" />;
    if (label.includes("Thermostat")) return <Thermometer className="w-4 h-4" />;
    return <Zap className="w-4 h-4" />;
  }
  if (label.includes("Gateway")) return <Wifi className="w-4 h-4" />;
  if (label.includes("Server")) return <Server className="w-4 h-4" />;
  if (label.includes("C&C")) return <Zap className="w-4 h-4" />;
  if (label.includes("Cloud")) return <Cloud className="w-4 h-4" />;
  if (label.includes("IDS") || label.includes("IPS") || label.includes("WAF"))
    return <Shield className="w-4 h-4" />;
  if (label.includes("Workstation") || label.includes("PC"))
    return <Laptop className="w-4 h-4" />;
  if (label.includes("Sensor") || label.includes("Camera"))
    return <Camera className="w-4 h-4" />;
  return <Cpu className="w-4 h-4" />;
};

// KpiCard Component
const KpiCard: React.FC<{
  title: string;
  value: string | number;
  delta?: number;
  icon: React.ReactNode;
  subtitle?: string;
  trend?: "up" | "down" | "stable";
}> = ({ title, value, delta, icon, subtitle, trend }) => {
  const trendColor =
    trend === "up"
      ? "text-red-400"
      : trend === "down"
        ? "text-emerald-400"
        : "text-white/50";
  const TrendIcon = trend === "up" ? TrendingUp : trend === "down" ? TrendingDown : null;
  return (
    <div className="group bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-4 hover:border-white/20 transition-all duration-300 hover:shadow-lg hover:shadow-white/5">
      <div className="flex items-center justify-between mb-2">
        <div className="text-white/60 text-xs font-medium tracking-wide uppercase">
          {title}
        </div>
        <div className="p-2 rounded-lg bg-gradient-to-br from-blue-500/20 to-purple-500/20 text-blue-400 group-hover:scale-110 transition-transform duration-300">
          {icon}
        </div>
      </div>
      <div className="flex items-baseline gap-2 flex-wrap">
        <span className="text-2xl font-bold text-white tracking-tight">
          {value}
        </span>
        {delta !== undefined && delta !== 0 && (
          <span
            className={`text-xs flex items-center gap-0.5 font-medium ${delta >= 0 ? "text-red-400" : "text-emerald-400"
              }`}
          >
            {delta >= 0 ? (
              <TrendingUp className="w-3 h-3" />
            ) : (
              <TrendingDown className="w-3 h-3" />
            )}
            {Math.abs(delta)}%
          </span>
        )}
        {trend && TrendIcon && (
          <span
            className={`text-xs flex items-center gap-0.5 ${trendColor}`}
          >
            <TrendIcon className="w-3 h-3" />
          </span>
        )}
      </div>
      {subtitle && (
        <p className="text-white/40 text-xs mt-1">{subtitle}</p>
      )}
    </div>
  );
};

// Alert Component
const AlertItemComponent: React.FC<{
  alert: AlertItem;
  onDismiss: (id: string) => void;
}> = ({ alert, onDismiss }) => {
  const severityConfig = {
    critical: {
      icon: <AlertOctagon className="w-4 h-4" />,
      color: "red",
      bg: "bg-red-500/10",
      border: "border-red-500/30",
    },
    warning: {
      icon: <AlertTriangle className="w-4 h-4" />,
      color: "amber",
      bg: "bg-amber-500/10",
      border: "border-amber-500/30",
    },
    info: {
      icon: <CheckCircle className="w-4 h-4" />,
      color: "blue",
      bg: "bg-blue-500/10",
      border: "border-blue-500/30",
    },
  };
  const config = severityConfig[alert.severity];
  return (
    <div
      className={`p-3 rounded-lg border ${config.bg} ${config.border} hover:bg-opacity-20 transition-all duration-200 group`}
    >
      <div className="flex items-start gap-2">
        <div className={`text-${config.color}-400 mt-0.5`}>
          {config.icon}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-white font-medium truncate">
            {alert.message}
          </p>
          <div className="flex items-center gap-2 mt-1">
            <span className={`text-xs text-${config.color}-400`}>
              {alert.source}
            </span>
            <span className="text-xs text-white/50">
              {alert.timestamp.toLocaleTimeString()}
            </span>
          </div>
        </div>
        <button
          onClick={() => onDismiss(alert.id)}
          className="opacity-0 group-hover:opacity-100 transition-opacity text-white/50 hover:text-white"
        >
          <EyeOff className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

// Level Selector Component
const LevelSelector: React.FC<{
  currentLevel: ViewLevel;
  onLevelChange: (level: ViewLevel) => void;
}> = ({ currentLevel, onLevelChange }) => {
  const levels = [
    { id: "network" as const, label: "Network Centric", icon: <Network className="w-4 h-4" />, description: "View network-wide metrics, topology, and infrastructure health" },
    { id: "device" as const, label: "Device Centric", icon: <Cpu className="w-4 h-4" />, description: "Focus on individual device details, configurations, and relationships" },
    { id: "event" as const, label: "Event Centric", icon: <AlertOctagon className="w-4 h-4" />, description: "View security events, alerts, incidents, and threat analysis" },
  ];

  const getLevelColor = (levelId: ViewLevel) => {
    switch (levelId) {
      case "network": return "primary";
      case "device": return "success";
      case "event": return "warning";
      default: return "secondary";
    }
  };

  return (
    <Dropdown as={ButtonGroup}>
      <Dropdown.Toggle
        variant={getLevelColor(currentLevel)}
        className="d-flex align-items-center gap-2"
      >
        {levels.find(l => l.id === currentLevel)?.icon}
        <span className="d-none d-md-inline">
          {levels.find(l => l.id === currentLevel)?.label}
        </span>
      </Dropdown.Toggle>
      <Dropdown.Menu className="bg-dark border-white/20">
        {levels.map((level) => (
          <Dropdown.Item
            key={level.id}
            onClick={() => onLevelChange(level.id)}
            className={`d-flex align-items-center gap-2 ${currentLevel === level.id ? "active bg-primary" : "text-white"
              }`}
            style={{ backgroundColor: currentLevel === level.id ? "rgba(13, 110, 253, 0.2)" : "transparent" }}
          >
            {level.icon}
            <div>
              <div className="fw-medium">{level.label}</div>
              <small className="text-white/50 d-none d-md-block">{level.description}</small>
            </div>
          </Dropdown.Item>
        ))}
      </Dropdown.Menu>
    </Dropdown>
  );
};

// Network Centric View Component
const NetworkCentricView: React.FC<{
  nodes: NetworkNode[];
  edges: any[];
  stats: DashboardStats;
  regions: any[];
}> = ({ nodes, edges, stats, regions }) => {
  const criticalLinks = edges.filter((e: any) => e.isCritical).length;
  const totalBandwidth = nodes.reduce((acc, n) => {
    if (n.metadata?.bandwidth) {
      const bw = parseInt(n.metadata.bandwidth);
      return acc + (isNaN(bw) ? 0 : bw);
    }
    return acc;
  }, 0);

  return (
    <div className="network-centric-view">
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-xl border border-blue-500/20 p-4">
            <div className="d-flex align-items-center gap-3">
              <div className="p-3 bg-blue-500/20 rounded-lg">
                <Globe className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <div className="text-white/60 text-xs">Network Uptime</div>
                <div className="text-2xl fw-bold text-white">{stats.networkUptime}%</div>
                <div className="text-white/40 text-xs">SLA: 99.99%</div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-xl border border-purple-500/20 p-4">
            <div className="d-flex align-items-center gap-3">
              <div className="p-3 bg-purple-500/20 rounded-lg">
                <Signal className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <div className="text-white/60 text-xs">Active Nodes</div>
                <div className="text-2xl fw-bold text-white">{nodes.length}</div>
                <div className="text-white/40 text-xs">{criticalLinks} critical links</div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-xl border border-emerald-500/20 p-4">
            <div className="d-flex align-items-center gap-3">
              <div className="p-3 bg-emerald-500/20 rounded-lg">
                <Activity className="w-6 h-6 text-emerald-400" />
              </div>
              <div>
                <div className="text-white/60 text-xs">Total Bandwidth</div>
                <div className="text-2xl fw-bold text-white">{totalBandwidth}Gbps</div>
                <div className="text-white/40 text-xs">Aggregate capacity</div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-xl border border-amber-500/20 p-4">
            <div className="d-flex align-items-center gap-3">
              <div className="p-3 bg-amber-500/20 rounded-lg">
                <Users className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <div className="text-white/60 text-xs">Subscribers</div>
                <div className="text-2xl fw-bold text-white">{stats.subscribersAffected.toLocaleString()}</div>
                <div className="text-white/40 text-xs">Across all regions</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Regional Health Summary */}
      <div className="bg-white/5 rounded-xl border border-white/10 p-4 mb-4">
        <h6 className="text-white mb-3 d-flex align-items-center gap-2">
          <MapPin className="w-4 h-4" />
          Regional Network Health
        </h6>
        <div className="row g-2">
          {regions.map((region, idx) => (
            <div key={idx} className="col-md-3 col-sm-6">
              <div className={`p-3 rounded-lg border ${region.status === "critical" ? "bg-red-500/10 border-red-500/30" :
                  region.status === "warning" ? "bg-amber-500/10 border-amber-500/30" :
                    "bg-white/5 border-white/10"
                }`}>
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <span className="text-white fw-medium">{region.name}</span>
                  <Badge bg={region.status === "critical" ? "danger" : region.status === "warning" ? "warning" : "success"}>
                    {region.status}
                  </Badge>
                </div>
                <div className="text-white/60 small">
                  <div>Latency: {region.latency}ms</div>
                  <div>Packet Loss: {region.packetLoss}%</div>
                  <div>Subscribers: {region.subscribers}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Network Topology Summary */}
      <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
        <div className="p-3 border-bottom border-white/10">
          <h6 className="text-white mb-0 d-flex align-items-center gap-2">
            <Network className="w-4 h-4" />
            Network Infrastructure Summary
          </h6>
        </div>
        <div className="table-responsive">
          <Table className="mb-0 text-white" variant="dark">
            <thead className="bg-white/10">
              <tr>
                <th>Device Type</th>
                <th>Count</th>
                <th>Critical</th>
                <th>Warning</th>
                <th>Normal</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(
                nodes.reduce((acc, node) => {
                  const type = node.type;
                  if (!acc[type]) acc[type] = { total: 0, critical: 0, warning: 0, normal: 0 };
                  acc[type].total++;
                  if (node.status === "critical") acc[type].critical++;
                  if (node.status === "warning") acc[type].warning++;
                  if (node.status === "normal") acc[type].normal++;
                  return acc;
                }, {} as Record<string, any>)
              ).map(([type, counts]) => (
                <tr key={type}>
                  <td className="text-white">{type}</td>
                  <td className="text-white">{counts.total}</td>
                  <td className="text-danger">{counts.critical}</td>
                  <td className="text-warning">{counts.warning}</td>
                  <td className="text-success">{counts.normal}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
    </div>
  );
};

// Device Centric View Component
const DeviceCentricView: React.FC<{
  nodes: NetworkNode[];
  selectedNode: NetworkNode | null;
  onNodeSelect: (node: NetworkNode) => void;
  edges: any[];
}> = ({ nodes, selectedNode, onNodeSelect, edges }) => {
  const [deviceFilter, setDeviceFilter] = useState<string>("all");
  const [deviceSearch, setDeviceSearch] = useState<string>("");

  const filteredDevices = nodes.filter(node => {
    if (deviceFilter !== "all" && node.type !== deviceFilter) return false;
    if (deviceSearch && !node.label.toLowerCase().includes(deviceSearch.toLowerCase())) return false;
    return true;
  });

  const deviceTypes = Array.from(new Set(nodes.map(n => n.type)));

  const getConnectedDevices = (nodeId: string) => {
    return edges
      .filter(edge => edge.from.id === nodeId || edge.to.id === nodeId)
      .map(edge => edge.from.id === nodeId ? edge.to : edge.from);
  };

  return (
    <div className="device-centric-view">
      <div className="row g-3">
        {/* Device List Panel */}
        <div className="col-md-5 col-lg-4">
          <div className="bg-white/5 rounded-xl border border-white/10">
            <div className="p-3 border-bottom border-white/10">
              <div className="d-flex gap-2 flex-nowrap">
                <select
                  className="form-select form-select-sm bg-black border-white/20 text-white flex-grow-1"
                  value={deviceFilter}
                  onChange={(e) => setDeviceFilter(e.target.value)}
                >
                  <option value="all">All Devices ({nodes.length})</option>
                  {deviceTypes.map(type => (
                    <option key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)} ({nodes.filter(n => n.type === type).length})
                    </option>
                  ))}
                </select>
                <div className="input-group input-group-sm" style={{ width: "150px", flexWrap: "nowrap" }}>
                  <span className="input-group-text bg-black border-white/20 text-white">
                    <Search className="w-3 h-3" />
                  </span>
                  <input
                    type="text"
                    className="form-control bg-black border-white/20 text-white"
                    placeholder="Search..."
                    value={deviceSearch}
                    onChange={(e) => setDeviceSearch(e.target.value)}
                  />
                </div>
              </div>
            </div>
            <div style={{ maxHeight: "500px", overflowY: "auto" }}>
              {filteredDevices.map(device => (
                <div
                  key={device.id}
                  onClick={() => onNodeSelect(device)}
                  className={`p-3 border-bottom border-white/10 cursor-pointer transition-all ${selectedNode?.id === device.id
                      ? "bg-blue-500/20 border-l-4 border-l-blue-500"
                      : "hover:bg-white/10"
                    }`}
                >
                  <div className="d-flex align-items-center gap-2">
                    {getNodeIcon(device.label)}
                    <div className="flex-grow-1">
                      <div className="text-white fw-medium small">{device.label}</div>
                      <div className="text-white/40 text-xs">
                        {device.metadata?.ip || "No IP"} • {device.metadata?.location || "Unknown"}
                      </div>
                    </div>
                    <Badge bg={device.status === "critical" ? "danger" : device.status === "warning" ? "warning" : "success"}>
                      {device.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Device Details Panel */}
        <div className="col-md-7 col-lg-8">
          {selectedNode ? (
            <div className="bg-white/5 rounded-xl border border-white/10">
              <div className="p-3 border-bottom border-white/10 bg-gradient-to-r from-blue-500/10 to-purple-500/10">
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center gap-3">
                    <div className="p-2 bg-white/10 rounded-lg">
                      {getNodeIcon(selectedNode.label)}
                    </div>
                    <div>
                      <h5 className="text-white mb-0">{selectedNode.label}</h5>
                      <div className="text-white/50 small">
                        ID: {selectedNode.id} • Type: {selectedNode.type}
                      </div>
                    </div>
                  </div>
                  <Badge bg={selectedNode.status === "critical" ? "danger" : selectedNode.status === "warning" ? "warning" : "success"} className="px-3 py-2">
                    {selectedNode.status.toUpperCase()}
                  </Badge>
                </div>
              </div>
              <div className="p-3">
                <div className="row g-3">
                  <div className="col-md-6">
                    <div className="mb-3">
                      <div className="text-white/50 text-xs mb-1">IP Address</div>
                      <code className="text-white bg-black/50 px-2 py-1 rounded">{selectedNode.metadata?.ip || "N/A"}</code>
                    </div>
                    <div className="mb-3">
                      <div className="text-white/50 text-xs mb-1">Location</div>
                      <div className="text-white">{selectedNode.metadata?.location || "Unknown"}</div>
                    </div>
                    <div className="mb-3">
                      <div className="text-white/50 text-xs mb-1">Device Type</div>
                      <div className="text-white">{selectedNode.metadata?.deviceType || selectedNode.type}</div>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="mb-3">
                      <div className="text-white/50 text-xs mb-1">Uptime / Bandwidth</div>
                      <div className="text-white">{selectedNode.metadata?.uptime || "N/A"} • {selectedNode.metadata?.bandwidth || "N/A"}</div>
                    </div>
                    {selectedNode.threatLevel && selectedNode.threatLevel !== "none" && (
                      <div className="mb-3">
                        <div className="text-white/50 text-xs mb-1">Threat Level</div>
                        <Badge bg={selectedNode.threatLevel === "high" ? "danger" : "warning"} className="me-2">
                          {selectedNode.threatLevel?.toUpperCase()}
                        </Badge>
                        {selectedNode.threatType && <span className="text-white/60 text-xs">{selectedNode.threatType}</span>}
                      </div>
                    )}
                    {selectedNode.cause && (
                      <div className="mb-3">
                        <div className="text-white/50 text-xs mb-1">Root Cause</div>
                        <div className="text-amber-400 text-sm">{selectedNode.cause}</div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Connected Devices */}
                <div className="mt-3 pt-3 border-top border-white/10">
                  <div className="text-white/50 text-xs mb-2">Connected Devices ({getConnectedDevices(selectedNode.id).length})</div>
                  <div className="d-flex flex-wrap gap-2">
                    {getConnectedDevices(selectedNode.id).map(device => (
                      <Badge
                        key={device.id}
                        bg="secondary"
                        className="cursor-pointer px-3 py-2"
                        style={{ cursor: "pointer" }}
                        onClick={() => onNodeSelect(device)}
                      >
                        {device.label.length > 25 ? device.label.substring(0, 23) + "..." : device.label}
                      </Badge>
                    ))}
                    {getConnectedDevices(selectedNode.id).length === 0 && (
                      <span className="text-white/40 text-sm">No connected devices</span>
                    )}
                  </div>
                </div>

                {/* Remediation Action */}
                {selectedNode.fix && (
                  <div className="mt-3 pt-3 border-top border-white/10">
                    <div className="text-white/50 text-xs mb-2">Recommended Remediation</div>
                    <button className="btn btn-sm btn-danger w-100">
                      <Play className="w-3 h-3 me-1" />
                      {selectedNode.fix}
                    </button>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="bg-white/5 rounded-xl border border-white/10 p-5 text-center">
              <Cpu className="w-12 h-12 text-white/20 mx-auto mb-3" />
              <h6 className="text-white/50">Select a device to view details</h6>
              <p className="text-white/30 small">Click on any device from the list to see detailed information, connected devices, and remediation options.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Event Centric View Component
const EventCentricView: React.FC<{
  alerts: AlertItem[];
  nodes: NetworkNode[];
  onNodeSelect: (node: NetworkNode) => void;
}> = ({ alerts, nodes, onNodeSelect }) => {
  const [eventFilter, setEventFilter] = useState<string>("all");
  const [selectedEvent, setSelectedEvent] = useState<AlertItem | null>(null);

  const threatNodes = nodes.filter(n => n.threatLevel && n.threatLevel !== "none");
  const criticalEvents = alerts.filter(a => a.severity === "critical").length;
  const warningEvents = alerts.filter(a => a.severity === "warning").length;
  const infoEvents = alerts.filter(a => a.severity === "info").length;

  const filteredEvents = alerts.filter(event => {
    if (eventFilter === "all") return true;
    return event.severity === eventFilter;
  });

  const findRelatedNodes = (event: AlertItem): NetworkNode[] => {
    return nodes.filter(node =>
      event.message.toLowerCase().includes(node.label.toLowerCase()) ||
      event.source.toLowerCase().includes(node.type) ||
      (node.cause && event.message.toLowerCase().includes(node.cause.toLowerCase()))
    );
  };

  return (
    <div className="event-centric-view">
      {/* Event Statistics */}
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="bg-gradient-to-br from-red-500/10 to-rose-500/10 rounded-xl border border-red-500/20 p-3">
            <div className="d-flex align-items-center gap-3">
              <AlertOctagon className="w-5 h-5 text-red-400" />
              <div>
                <div className="text-white/60 text-xs">Critical Events</div>
                <div className="text-2xl fw-bold text-white">{criticalEvents}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-xl border border-amber-500/20 p-3">
            <div className="d-flex align-items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
              <div>
                <div className="text-white/60 text-xs">Warning Events</div>
                <div className="text-2xl fw-bold text-white">{warningEvents}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="bg-gradient-to-br from-blue-500/10 to-cyan-500/10 rounded-xl border border-blue-500/20 p-3">
            <div className="d-flex align-items-center gap-3">
              <InfoIcon className="w-5 h-5 text-blue-400" />
              <div>
                <div className="text-white/60 text-xs">Info Events</div>
                <div className="text-2xl fw-bold text-white">{infoEvents}</div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-xl border border-purple-500/20 p-3">
            <div className="d-flex align-items-center gap-3">
              <Zap className="w-5 h-5 text-purple-400" />
              <div>
                <div className="text-white/60 text-xs">Active Threats</div>
                <div className="text-2xl fw-bold text-white">{threatNodes.length}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="row g-3">
        {/* Events List */}
        <div className="col-md-5 col-lg-4">
          <div className="bg-white/5 rounded-xl border border-white/10">
            <div className="p-3 border-bottom border-white/10">
              <div className="d-flex gap-2">
                <select
                  className="form-select form-select-sm bg-black border-white/20 text-white"
                  value={eventFilter}
                  onChange={(e) => setEventFilter(e.target.value)}
                >
                  <option value="all">All Events ({alerts.length})</option>
                  <option value="critical">Critical ({criticalEvents})</option>
                  <option value="warning">Warning ({warningEvents})</option>
                  <option value="info">Info ({infoEvents})</option>
                </select>
              </div>
            </div>
            <div style={{ maxHeight: "500px", overflowY: "auto" }}>
              {filteredEvents.map(event => (
                <div
                  key={event.id}
                  onClick={() => setSelectedEvent(event)}
                  className={`p-3 border-bottom border-white/10 cursor-pointer transition-all ${selectedEvent?.id === event.id
                      ? "bg-blue-500/20 border-l-4 border-l-blue-500"
                      : "hover:bg-white/10"
                    }`}
                >
                  <div className="d-flex gap-2">
                    <div className="mt-0.5">
                      {event.severity === "critical" ? (
                        <AlertOctagon className="w-4 h-4 text-red-400" />
                      ) : event.severity === "warning" ? (
                        <AlertTriangle className="w-4 h-4 text-amber-400" />
                      ) : (
                        <CheckCircle className="w-4 h-4 text-blue-400" />
                      )}
                    </div>
                    <div className="flex-grow-1">
                      <div className="text-white small fw-medium">{event.message}</div>
                      <div className="d-flex gap-2 mt-1">
                        <span className="text-white/40 text-xs">{event.source}</span>
                        <span className="text-white/30 text-xs">•</span>
                        <span className="text-white/40 text-xs">{event.timestamp.toLocaleTimeString()}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Event Details */}
        <div className="col-md-7 col-lg-8">
          {selectedEvent ? (
            <div className="bg-white/5 rounded-xl border border-white/10">
              <div className={`p-3 border-bottom ${selectedEvent.severity === "critical" ? "bg-red-500/10 border-red-500/30" :
                  selectedEvent.severity === "warning" ? "bg-amber-500/10 border-amber-500/30" :
                    "bg-blue-500/10 border-blue-500/30"
                }`}>
                <div className="d-flex align-items-center gap-2">
                  {selectedEvent.severity === "critical" ? (
                    <AlertOctagon className="w-5 h-5 text-red-400" />
                  ) : selectedEvent.severity === "warning" ? (
                    <AlertTriangle className="w-5 h-5 text-amber-400" />
                  ) : (
                    <CheckCircle className="w-5 h-5 text-blue-400" />
                  )}
                  <div>
                    <h6 className="text-white mb-0">{selectedEvent.message}</h6>
                    <div className="text-white/50 small">
                      {selectedEvent.source} • {selectedEvent.timestamp.toLocaleString()}
                    </div>
                  </div>
                </div>
              </div>
              <div className="p-3">
                <div className="mb-3">
                  <div className="text-white/50 text-xs mb-2">Related Devices</div>
                  <div className="d-flex flex-wrap gap-2">
                    {findRelatedNodes(selectedEvent).map(node => (
                      <Badge
                        key={node.id}
                        bg="secondary"
                        className="cursor-pointer px-3 py-2"
                        style={{ cursor: "pointer" }}
                        onClick={() => onNodeSelect(node)}
                      >
                        {node.label}
                      </Badge>
                    ))}
                    {findRelatedNodes(selectedEvent).length === 0 && (
                      <span className="text-white/40 text-sm">No related devices found</span>
                    )}
                  </div>
                </div>

                {/* Threat Intelligence */}
                <div className="bg-black/30 rounded-lg p-3 mb-3">
                  <div className="text-white/50 text-xs mb-2">Threat Intelligence</div>
                  <div className="d-flex gap-3 flex-wrap">
                    <div><span className="text-white/40">Severity:</span> <Badge bg={selectedEvent.severity === "critical" ? "danger" : selectedEvent.severity === "warning" ? "warning" : "info"}>{selectedEvent.severity.toUpperCase()}</Badge></div>
                    <div><span className="text-white/40">Source:</span> <span className="text-white">{selectedEvent.source}</span></div>
                    <div><span className="text-white/40">Time:</span> <span className="text-white">{selectedEvent.timestamp.toLocaleString()}</span></div>
                  </div>
                </div>

                {/* Recommended Actions */}
                <div className="bg-gradient-to-r from-red-500/10 to-amber-500/10 rounded-lg p-3">
                  <div className="text-white/50 text-xs mb-2">Recommended Actions</div>
                  <div className="d-flex gap-2 flex-wrap">
                    <button className="btn btn-sm btn-danger">Investigate</button>
                    <button className="btn btn-sm btn-warning">Acknowledge</button>
                    <button className="btn btn-sm btn-secondary">Escalate</button>
                    <button className="btn btn-sm btn-info">View Logs</button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-white/5 rounded-xl border border-white/10 p-5 text-center">
              <AlertOctagon className="w-12 h-12 text-white/20 mx-auto mb-3" />
              <h6 className="text-white/50">Select an event to view details</h6>
              <p className="text-white/30 small">Click on any event from the list to see detailed information, related devices, and recommended actions.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Simple Info Icon
const InfoIcon = (props: any) => (
  <svg {...props} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

// ==================== PROFESSIONAL GRAPH WITH SMOOTH ZOOM ====================

interface TransformState {
  x: number;
  y: number;
  k: number;
}

// Professional zoom/pan hook with smooth animations
const useProfessionalZoomPan = (containerRef: React.RefObject<HTMLDivElement | null>) => {
  const [transform, setTransform] = useState<TransformState>({ x: 0, y: 0, k: 1 });
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0, transformX: 0, transformY: 0 });
  const animationRef = useRef<number | undefined>(undefined);
  const transformRef = useRef(transform);

  useEffect(() => {
    transformRef.current = transform;
  }, [transform]);

  // Smooth easing function
  const easeInOutCubic = (t: number): number => {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  };

  // Animate to target transform with smooth easing
  const animateTo = useCallback((
    targetX: number,
    targetY: number,
    targetK: number,
    duration: number = 300,
    onComplete?: () => void
  ) => {
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
    }

    const startX = transformRef.current.x;
    const startY = transformRef.current.y;
    const startK = transformRef.current.k;
    const startTime = performance.now();

    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(1, elapsed / duration);
      const eased = easeInOutCubic(progress);

      const newX = startX + (targetX - startX) * eased;
      const newY = startY + (targetY - startY) * eased;
      const newK = startK + (targetK - startK) * eased;

      setTransform({ x: newX, y: newY, k: newK });

      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        if (onComplete) onComplete();
        animationRef.current = undefined;
      }
    };

    animationRef.current = requestAnimationFrame(animate);
  }, []);

  // Mouse wheel zoom with cursor position
  const handleWheel = useCallback((e: WheelEvent) => {
    e.preventDefault();

    const delta = e.deltaY > 0 ? -0.08 : 0.08;
    const newScale = Math.min(Math.max(transformRef.current.k + delta, 0.3), 3);

    if (Math.abs(newScale - transformRef.current.k) < 0.001) return;

    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;

      // Calculate new position to zoom toward mouse cursor
      const scaleRatio = newScale / transformRef.current.k;
      const newX = mouseX - (mouseX - transformRef.current.x) * scaleRatio;
      const newY = mouseY - (mouseY - transformRef.current.y) * scaleRatio;

      animateTo(newX, newY, newScale, 250);
    } else {
      animateTo(transformRef.current.x, transformRef.current.y, newScale, 250);
    }
  }, [containerRef, animateTo]);

  // Mouse down for panning
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (e.button === 0) {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
        animationRef.current = undefined;
      }
      setIsPanning(true);
      panStartRef.current = {
        x: e.clientX,
        y: e.clientY,
        transformX: transformRef.current.x,
        transformY: transformRef.current.y,
      };
    }
  }, []);

  // Mouse move for panning
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isPanning) return;

    const dx = e.clientX - panStartRef.current.x;
    const dy = e.clientY - panStartRef.current.y;

    setTransform({
      ...transformRef.current,
      x: panStartRef.current.transformX + dx,
      y: panStartRef.current.transformY + dy,
    });
  }, [isPanning]);

  // Mouse up to end panning
  const handleMouseUp = useCallback(() => {
    setIsPanning(false);
  }, []);

  // Reset view with animation
  const resetView = useCallback(() => {
    animateTo(0, 0, 1, 400);
  }, [animateTo]);

  // Zoom in with animation
  const zoomIn = useCallback(() => {
    const newScale = Math.min(transformRef.current.k + 0.2, 3);
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const scaleRatio = newScale / transformRef.current.k;
      const newX = centerX - (centerX - transformRef.current.x) * scaleRatio;
      const newY = centerY - (centerY - transformRef.current.y) * scaleRatio;
      animateTo(newX, newY, newScale, 250);
    } else {
      animateTo(transformRef.current.x, transformRef.current.y, newScale, 250);
    }
  }, [containerRef, animateTo]);

  // Zoom out with animation
  const zoomOut = useCallback(() => {
    const newScale = Math.max(transformRef.current.k - 0.2, 0.3);
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const centerX = rect.width / 2;
      const centerY = rect.height / 2;
      const scaleRatio = newScale / transformRef.current.k;
      const newX = centerX - (centerX - transformRef.current.x) * scaleRatio;
      const newY = centerY - (centerY - transformRef.current.y) * scaleRatio;
      animateTo(newX, newY, newScale, 250);
    } else {
      animateTo(transformRef.current.x, transformRef.current.y, newScale, 250);
    }
  }, [containerRef, animateTo]);

  // Focus on a specific node
  const focusOnNode = useCallback((node: NetworkNode, containerRect: DOMRect) => {
    const targetScale = 1.8;
    const centerX = containerRect.width / 2;
    const centerY = containerRect.height / 2;
    const newX = centerX - node.x * targetScale;
    const newY = centerY - node.y * targetScale;
    animateTo(newX, newY, targetScale, 500);
  }, [animateTo]);

  // Cleanup
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, []);

  return {
    transform,
    handleWheel,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    resetView,
    zoomIn,
    zoomOut,
    focusOnNode,
    isPanning,
  };
};

// Professional Zoomable Graph Component
const ProfessionalZoomableGraph: React.FC<{
  nodes: NetworkNode[];
  edges: { from: NetworkNode; to: NetworkNode; isCritical: boolean }[];
  onNodeClick: (node: NetworkNode) => void;
  onNodeHover: (node: NetworkNode | null) => void;
  selectedNodeId: string | null;
  hoveredNodeId: string | null;
  focusedNodeId?: string | null;
}> = ({
  nodes,
  edges,
  onNodeClick,
  onNodeHover,
  selectedNodeId,
  hoveredNodeId,
  focusedNodeId,
}) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const svgRef = useRef<SVGSVGElement>(null);
    const [particles, setParticles] = useState<{ id: number; edgeIndex: number; progress: number; speed: number }[]>([]);
    const particleIdRef = useRef(0);
    const particleAnimationRef = useRef<number | undefined>(undefined);

    const {
      transform,
      handleWheel,
      handleMouseDown,
      handleMouseMove,
      handleMouseUp,
      resetView,
      zoomIn,
      zoomOut,
      focusOnNode,
      isPanning,
    } = useProfessionalZoomPan(containerRef);

    // Get connected node IDs for focus mode
    const getConnectedNodeIds = useCallback((nodeId: string | null) => {
      if (!nodeId) return new Set<string>();
      const connectedIds = new Set<string>([nodeId]);
      edges.forEach((edge) => {
        if (edge.from.id === nodeId) connectedIds.add(edge.to.id);
        if (edge.to.id === nodeId) connectedIds.add(edge.from.id);
      });
      return connectedIds;
    }, [edges]);

    const connectedNodesSet = getConnectedNodeIds(focusedNodeId || selectedNodeId);
    const isFocusedMode = !!(focusedNodeId || selectedNodeId);

    // Particle animation
    useEffect(() => {
      let lastTimestamp = 0;
      const animateParticles = (timestamp: number) => {
        if (!lastTimestamp) {
          lastTimestamp = timestamp;
          particleAnimationRef.current = requestAnimationFrame(animateParticles);
          return;
        }

        const delta = Math.min(100, timestamp - lastTimestamp);
        if (delta > 0) {
          setParticles(prev => {
            const updated = prev
              .map(p => ({ ...p, progress: p.progress + p.speed * (delta / 16) }))
              .filter(p => p.progress < 1);

            if (edges.length > 0 && Math.random() < 0.08 * (delta / 16)) {
              const newParticle = {
                id: particleIdRef.current++,
                edgeIndex: Math.floor(Math.random() * edges.length),
                progress: 0,
                speed: 0.006 + Math.random() * 0.01,
              };
              return [...updated, newParticle];
            }
            return updated;
          });
        }

        lastTimestamp = timestamp;
        particleAnimationRef.current = requestAnimationFrame(animateParticles);
      };

      particleAnimationRef.current = requestAnimationFrame(animateParticles);
      return () => {
        if (particleAnimationRef.current) cancelAnimationFrame(particleAnimationRef.current);
      };
    }, [edges]);

    // Particle position calculation
    const getParticlePosition = useCallback((
      edge: { from: NetworkNode; to: NetworkNode },
      progress: number
    ) => {
      const fromNode = nodes.find((n) => n.id === edge.from.id);
      const toNode = nodes.find((n) => n.id === edge.to.id);
      if (!fromNode || !toNode) return { x: 0, y: 0 };
      return {
        x: fromNode.x + (toNode.x - fromNode.x) * progress,
        y: fromNode.y + (toNode.y - fromNode.y) * progress,
      };
    }, [nodes]);

    // Attach wheel event listener
    useEffect(() => {
      const container = containerRef.current;
      if (container) {
        container.addEventListener('wheel', handleWheel as any, { passive: false });
        return () => container.removeEventListener('wheel', handleWheel as any);
      }
    }, [handleWheel]);

    // Handle node click with focus
    const handleNodeClickWithFocus = useCallback((node: NetworkNode) => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        focusOnNode(node, rect);
      }
      onNodeClick(node);
    }, [focusOnNode, onNodeClick]);

    // Calculate if label should be visible based on zoom level
    const showDetailedLabels = transform.k >= 0.8;
    const showIpLabels = transform.k >= 1.2;

    // Render edges
    const renderedEdges = useMemo(() => {
      return edges.map((edge, idx) => {
        const fromNode = nodes.find((n) => n.id === edge.from.id);
        const toNode = nodes.find((n) => n.id === edge.to.id);
        if (!fromNode || !toNode) return null;

        const isConnectedToFocus = !isFocusedMode || connectedNodesSet.has(edge.from.id) || connectedNodesSet.has(edge.to.id);
        const shouldDim = isFocusedMode && !isConnectedToFocus;
        const isCritical = edge.isCritical;

        const strokeColor = isCritical ? "#ef4444" : "#3b82f6";
        const strokeOpacity = shouldDim ? 0.15 : isCritical ? 0.8 : 0.4;
        const strokeWidth = isCritical ? 2.5 : 1.5;

        return (
          <g key={`edge-${idx}`} opacity={shouldDim ? 0.4 : 1}>
            {/* Main line */}
            <line
              x1={fromNode.x}
              y1={fromNode.y}
              x2={toNode.x}
              y2={toNode.y}
              stroke={strokeColor}
              strokeOpacity={strokeOpacity}
              strokeWidth={strokeWidth}
              strokeDasharray={isCritical ? "8,4" : "none"}
            />

            {/* Animated dash for critical links */}
            {isCritical && !shouldDim && (
              <line
                x1={fromNode.x}
                y1={fromNode.y}
                x2={toNode.x}
                y2={toNode.y}
                stroke="#ef4444"
                strokeOpacity={0.9}
                strokeWidth={2}
                strokeDasharray="6,12"
                className="critical-dash"
              />
            )}

            {/* Particles */}
            {!shouldDim && particles
              .filter((p) => edges[p.edgeIndex] === edge)
              .map((particle) => {
                const pos = getParticlePosition(edge, particle.progress);
                return (
                  <circle
                    key={`particle-${particle.id}`}
                    cx={pos.x}
                    cy={pos.y}
                    r="3"
                    fill={isCritical ? "#ef4444" : "#60a5fa"}
                    opacity={0.8}
                  />
                );
              })}
          </g>
        );
      });
    }, [edges, nodes, isFocusedMode, connectedNodesSet, particles, getParticlePosition]);

    // Render nodes
    const renderedNodes = useMemo(() => {
      return nodes.map((node) => {
        const isSelected = selectedNodeId === node.id;
        const isHovered = hoveredNodeId === node.id;
        const isConnected = connectedNodesSet.has(node.id);
        const shouldHighlight = !isFocusedMode || isConnected;
        const shouldDim = isFocusedMode && !isConnected;

        const nodeColor = node.status === "critical" ? "#ef4444" : node.status === "warning" ? "#f59e0b" : "#10b981";
        const nodeScale = isSelected ? 1.2 : (isHovered ? 1.1 : 1);
        const opacity = shouldDim ? 0.4 : 1;

        return (
          <g
            key={node.id}
            transform={`translate(${node.x}, ${node.y}) scale(${nodeScale})`}
            onClick={() => handleNodeClickWithFocus(node)}
            onMouseEnter={() => onNodeHover(node)}
            onMouseLeave={() => onNodeHover(null)}
            style={{ cursor: "pointer", opacity, transition: "transform 0.2s ease" }}
          >
            {/* Pulsing ring for critical nodes */}
            {node.status === "critical" && shouldHighlight && (
              <circle r="28" fill="none" stroke="#ef4444" strokeWidth="1.5" opacity="0.6">
                <animate
                  attributeName="r"
                  values="24;32;24"
                  dur="1.5s"
                  repeatCount="indefinite"
                />
                <animate
                  attributeName="opacity"
                  values="0.6;0.1;0.6"
                  dur="1.5s"
                  repeatCount="indefinite"
                />
              </circle>
            )}

            {/* Selection ring */}
            {(isSelected || isHovered) && (
              <circle
                r="26"
                fill="none"
                stroke={isSelected ? "#3b82f6" : "#8b5cf6"}
                strokeWidth="3"
                opacity="0.9"
              />
            )}

            {/* Main node circle */}
            <circle
              r="18"
              fill={`url(#grad-${node.status})`}
              stroke="rgba(255,255,255,0.2)"
              strokeWidth="1.5"
            />

            {/* Inner circle */}
            <circle r="7" fill="rgba(255,255,255,0.15)" />

            {/* Icon */}
            <foreignObject x="-11" y="-11" width="22" height="22">
              <div className="w-full h-full flex items-center justify-center text-white">
                {getNodeIcon(node.label)}
              </div>
            </foreignObject>

            {/* Status indicator */}
            <circle
              cx="15"
              cy="-15"
              r="5"
              fill={nodeColor}
              stroke="rgba(0,0,0,0.5)"
              strokeWidth="1.5"
            />

            {/* Node Label - Always visible */}
            <rect
              x="-85"
              y="24"
              width="170"
              height={showDetailedLabels ? (showIpLabels ? 36 : 24) : 20}
              rx="6"
              fill="rgba(0,0,0,0.85)"
              stroke="rgba(255,255,255,0.15)"
              strokeWidth="0.5"
            />

            {/* Node name */}
            <text
              x="0"
              y={showDetailedLabels ? (showIpLabels ? 32 : 32) : 30}
              textAnchor="middle"
              className="text-[10px] fill-white font-medium"
              dominantBaseline="middle"
            >
              {node.label.length > 20 ? node.label.substring(0, 18) + "..." : node.label}
            </text>

            {/* IP Address - Visible when zoomed in */}
            {showDetailedLabels && showIpLabels && node.metadata?.ip && (
              <text
                x="0"
                y="46"
                textAnchor="middle"
                className="text-[8px] fill-white/60"
                dominantBaseline="middle"
              >
                {node.metadata.ip}
              </text>
            )}
          </g>
        );
      });
    }, [nodes, selectedNodeId, hoveredNodeId, isFocusedMode, connectedNodesSet, handleNodeClickWithFocus, onNodeHover, showDetailedLabels, showIpLabels]);

    return (
      <div
        ref={containerRef}
        className="position-relative w-100 h-100 overflow-hidden"
        style={{
          background: "radial-gradient(circle at center, #0a0a0a 0%, #000000 100%)",
          cursor: isPanning ? "grabbing" : "grab",
        }}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
      >
        <svg
          ref={svgRef}
          className="w-100 h-100"
          style={{
            transform: `translate(${transform.x}px, ${transform.y}px) scale(${transform.k})`,
            transformOrigin: "0 0",
            willChange: "transform",
          }}
        >
          <defs>
            {/* Gradients for node statuses */}
            <radialGradient id="grad-critical" cx="30%" cy="30%" r="70%">
              <stop offset="0%" stopColor="#ef4444" stopOpacity="0.95" />
              <stop offset="50%" stopColor="#dc2626" stopOpacity="0.7" />
              <stop offset="100%" stopColor="#7f1d1d" stopOpacity="0.3" />
            </radialGradient>
            <radialGradient id="grad-warning" cx="30%" cy="30%" r="70%">
              <stop offset="0%" stopColor="#f59e0b" stopOpacity="0.95" />
              <stop offset="50%" stopColor="#d97706" stopOpacity="0.7" />
              <stop offset="100%" stopColor="#78350f" stopOpacity="0.3" />
            </radialGradient>
            <radialGradient id="grad-normal" cx="30%" cy="30%" r="70%">
              <stop offset="0%" stopColor="#10b981" stopOpacity="0.95" />
              <stop offset="50%" stopColor="#059669" stopOpacity="0.7" />
              <stop offset="100%" stopColor="#064e3b" stopOpacity="0.3" />
            </radialGradient>

            {/* Grid pattern */}
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path
                d="M 40 0 L 0 0 0 40"
                fill="none"
                stroke="rgba(255,255,255,0.03)"
                strokeWidth="0.5"
              />
              <circle cx="0" cy="0" r="0.5" fill="rgba(255,255,255,0.03)" />
            </pattern>
          </defs>

          <rect width="100%" height="100%" fill="url(#grid)" />
          {renderedEdges}
          {renderedNodes}
        </svg>

        {/* Zoom Controls */}
        <div className="position-absolute bottom-4 end-4 d-flex gap-2">
          <div className="bg-black/80 backdrop-blur-md rounded-lg border border-white/20 p-1 d-flex gap-1">
            <button
              onClick={zoomOut}
              className="btn btn-sm p-2 text-white hover:text-white transition-colors"
              title="Zoom Out"
              style={{ width: "36px", height: "36px" }}
            >
              <Minus className="w-4 h-4" />
            </button>
            <button
              onClick={resetView}
              className="btn btn-sm p-2 text-white hover:text-white transition-colors"
              title="Reset View"
              style={{ width: "36px", height: "36px" }}
            >
              <Home className="w-4 h-4" />
            </button>
            <button
              onClick={zoomIn}
              className="btn btn-sm p-2 text-white hover:text-white transition-colors"
              title="Zoom In"
              style={{ width: "36px", height: "36px" }}
            >
              <Plus className="w-4 h-4" />
            </button>
          </div>
          <div className="bg-black/80 backdrop-blur-md rounded-lg px-3 py-2 text-white/90 text-sm font-mono border border-white/20">
            {Math.round(transform.k * 100)}%
          </div>
        </div>

        {/* Info Overlays */}
        <div className="position-absolute bottom-4 start-4 bg-black/70 backdrop-blur-md rounded-md px-3 py-1.5 text-white/70 text-xs border border-white/15">
          <Signal className="w-3 h-3 d-inline me-1" />
          {nodes.length} NODES • {edges.length} LINKS
          {isFocusedMode && (
            <span className="ms-2 text-blue-400">
              • FOCUS MODE
            </span>
          )}
        </div>

        <div className="position-absolute top-4 end-4 bg-black/70 backdrop-blur-md rounded-md px-3 py-1.5 text-white/70 text-xs border border-white/15">
          <div className="d-flex gap-3">
            <span className="d-flex align-items-center gap-1">
              <span className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></span>
              Critical
            </span>
            <span className="d-flex align-items-center gap-1">
              <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
              Warning
            </span>
            <span className="d-flex align-items-center gap-1">
              <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
              Normal
            </span>
          </div>
        </div>

        {/* Instructions */}
        <div className="position-absolute top-4 start-4 bg-black/70 backdrop-blur-md rounded-md px-3 py-1.5 text-white/60 text-xs border border-white/15">
          <span className="d-flex align-items-center gap-2">
            <span>🖱️ Drag to pan</span>
            <span>•</span>
            <span>📌 Scroll to zoom</span>
            <span>•</span>
            <span>✨ Click node to focus</span>
          </span>
        </div>

        {/* CSS for critical dash animation */}
        <style>{`
        .critical-dash {
          animation: dash 1s linear infinite;
        }
        @keyframes dash {
          to {
            stroke-dashoffset: -18;
          }
        }
      `}</style>
      </div>
    );
  };

// ==================== NODE DETAILS PANEL COMPONENT ====================
// This panel displays detailed information about the selected node and its connected devices
const NodeDetailsPanel: React.FC<{
  selectedNode: NetworkNode | null;
  connectedDevices: NetworkNode[];
  onNodeSelect: (node: NetworkNode) => void;
  onClose: () => void;
}> = ({ selectedNode, connectedDevices, onNodeSelect, onClose }) => {
  if (!selectedNode) return null;

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case "critical": return "bg-danger";
      case "warning": return "bg-warning text-dark";
      default: return "bg-success";
    }
  };

  return (
    <div className="bg-gradient-to-br from-gray-900/95 to-black/95 backdrop-blur-xl rounded-xl border border-white/20 shadow-2xl overflow-hidden mt-4">
      {/* Header with gradient */}
      <div className="bg-gradient-to-r from-blue-600/30 to-purple-600/30 p-4 border-b border-white/20">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center gap-3">
            <div className="p-2 bg-white/10 rounded-lg">
              {selectedNode && getNodeIcon(selectedNode.label)}
            </div>
            <div>
              <h4 className="text-white mb-0 fw-bold">{selectedNode.label}</h4>
              <div className="text-white/50 small">
                ID: {selectedNode.id} • Type: {selectedNode.type}
              </div>
            </div>
          </div>
          <div className="d-flex gap-2">
            <Badge className={`${getStatusBadgeClass(selectedNode.status)} px-3 py-2`}>
              {selectedNode.status.toUpperCase()}
            </Badge>
            <button
              onClick={onClose}
              className="btn btn-sm btn-outline-light border-white/30 text-white/70 hover:text-white"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="row g-4">
          {/* Left Column - Device Details */}
          <div className="col-md-6">
            <div className="bg-black/30 rounded-lg p-3 mb-3">
              <h6 className="text-white/70 text-xs uppercase tracking-wide mb-3 d-flex align-items-center gap-2">
                <HardDrive className="w-3 h-3" />
                Device Information
              </h6>
              <div className="space-y-2">
                <div className="d-flex justify-content-between border-b border-white/10 pb-2">
                  <span className="text-white/50 text-sm">IP Address</span>
                  <code className="text-white/80 text-sm bg-black/50 px-2 py-0.5 rounded">
                    {selectedNode.metadata?.ip || "N/A"}
                  </code>
                </div>
                <div className="d-flex justify-content-between border-b border-white/10 pb-2">
                  <span className="text-white/50 text-sm">Location</span>
                  <span className="text-white/80 text-sm">{selectedNode.metadata?.location || "Unknown"}</span>
                </div>
                <div className="d-flex justify-content-between border-b border-white/10 pb-2">
                  <span className="text-white/50 text-sm">Device Type</span>
                  <span className="text-white/80 text-sm">{selectedNode.metadata?.deviceType || selectedNode.type}</span>
                </div>
                <div className="d-flex justify-content-between border-b border-white/10 pb-2">
                  <span className="text-white/50 text-sm">Uptime</span>
                  <span className="text-white/80 text-sm">{selectedNode.metadata?.uptime || "N/A"}</span>
                </div>
                <div className="d-flex justify-content-between border-b border-white/10 pb-2">
                  <span className="text-white/50 text-sm">Bandwidth</span>
                  <span className="text-white/80 text-sm">{selectedNode.metadata?.bandwidth || "N/A"}</span>
                </div>
                {selectedNode.metadata?.os && (
                  <div className="d-flex justify-content-between border-b border-white/10 pb-2">
                    <span className="text-white/50 text-sm">OS</span>
                    <span className="text-white/80 text-sm">{selectedNode.metadata.os}</span>
                  </div>
                )}
              </div>
            </div>

            {/* Threat Information */}
            {selectedNode.threatLevel && selectedNode.threatLevel !== "none" && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3">
                <h6 className="text-red-400 text-xs uppercase tracking-wide mb-3 d-flex align-items-center gap-2">
                  <AlertOctagon className="w-3 h-3" />
                  Threat Intelligence
                </h6>
                <div className="space-y-2">
                  <div className="d-flex justify-content-between">
                    <span className="text-white/50 text-sm">Threat Level</span>
                    <Badge bg={selectedNode.threatLevel === "high" ? "danger" : "warning"}>
                      {selectedNode.threatLevel.toUpperCase()}
                    </Badge>
                  </div>
                  {selectedNode.threatType && (
                    <div className="d-flex justify-content-between">
                      <span className="text-white/50 text-sm">Threat Type</span>
                      <span className="text-white/80 text-sm">{selectedNode.threatType}</span>
                    </div>
                  )}
                  {selectedNode.cause && (
                    <div>
                      <span className="text-white/50 text-sm">Root Cause</span>
                      <p className="text-amber-400 text-sm mt-1 mb-0">{selectedNode.cause}</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Connected Devices */}
          <div className="col-md-6">
            <div className="bg-black/30 rounded-lg p-3">
              <h6 className="text-white/70 text-xs uppercase tracking-wide mb-3 d-flex align-items-center gap-2">
                <Link className="w-3 h-3" />
                Connected Devices ({connectedDevices.length})
              </h6>
              {connectedDevices.length > 0 ? (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {connectedDevices.map(device => (
                    <div
                      key={device.id}
                      onClick={() => onNodeSelect(device)}
                      className="d-flex align-items-center justify-content-between p-2 rounded-lg bg-white/5 hover:bg-white/10 cursor-pointer transition-all border border-white/10 hover:border-white/30"
                    >
                      <div className="d-flex align-items-center gap-2">
                        {getNodeIcon(device.label)}
                        <div>
                          <div className="text-white text-sm fw-medium">{device.label}</div>
                          <div className="text-white/40 text-xs">{device.metadata?.ip || "No IP"} • {device.type}</div>
                        </div>
                      </div>
                      <Badge className={device.status === "critical" ? "bg-danger" : device.status === "warning" ? "bg-warning text-dark" : "bg-success"}>
                        {device.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <Link className="w-8 h-8 text-white/20 mx-auto mb-2" />
                  <p className="text-white/40 text-sm mb-0">No connected devices</p>
                </div>
              )}
            </div>

            {/* Remediation Action */}
            {selectedNode.fix && (
              <div className="mt-3 bg-gradient-to-r from-amber-500/10 to-red-500/10 rounded-lg p-3 border border-amber-500/30">
                <h6 className="text-amber-400 text-xs uppercase tracking-wide mb-2 d-flex align-items-center gap-2">
                  <Play className="w-3 h-3" />
                  Recommended Remediation
                </h6>
                <button className="btn btn-danger w-100 btn-sm">
                  <Play className="w-3 h-3 me-1" />
                  {selectedNode.fix}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Dashboard Component
export const DashboardV2 = () => {
  const [ipCount, setIpCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [dnsResponses, setDnsResponses] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(1);
  const [pageSize, setPageSize] = useState(1);
  const [activeItem, setActiveItem] = useState<any>(null);
  const [selectedNode, setSelectedNode] = useState<NetworkNode | null>(null);
  const [hoveredNode, setHoveredNode] = useState<NetworkNode | null>(null);
  const [hoveredAction, setHoveredAction] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showAlerts, setShowAlerts] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [focusedNodeId, setFocusedNodeId] = useState<string | null>(null);
  const [alertsList, setAlertsList] = useState<AlertItem[]>(alerts);
  const [currentViewLevel, setCurrentViewLevel] = useState<ViewLevel>("network");
  const [connectedDevices, setConnectedDevices] = useState<NetworkNode[]>([]);

  const graphContainerRef = useRef<HTMLDivElement>(null);
  const edges = useMemo(() => generateEdges(initialNodes), []);

  // Filter nodes based on status and search
  const filteredNodes = useMemo(() => {
    let filtered = initialNodes;
    if (filterStatus !== "all") {
      filtered = filtered.filter((node) => node.status === filterStatus);
    }
    if (searchTerm) {
      filtered = filtered.filter(
        (node) =>
          node.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
          node.id.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return filtered;
  }, [filterStatus, searchTerm]);

  // Get connected devices for the selected node
  const getConnectedDevicesForNode = useCallback((node: NetworkNode | null): NetworkNode[] => {
    if (!node) return [];
    return edges
      .filter(edge => edge.from.id === node.id || edge.to.id === node.id)
      .map(edge => edge.from.id === node.id ? edge.to : edge.from);
  }, [edges]);

  // Update connected devices when selected node changes
  useEffect(() => {
    if (selectedNode) {
      const devices = getConnectedDevicesForNode(selectedNode);
      setConnectedDevices(devices);
    } else {
      setConnectedDevices([]);
    }
  }, [selectedNode, getConnectedDevicesForNode]);

  // Mock API calls
  const fetchDnsResponses = useCallback(async () => {
    setIsLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 300));
      setIpCount(1247);
      setTotalCount(25);
      setPageSize(10);
      setDnsResponses([]);
    } catch (error) {
      console.error("Error fetching DNS responses:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDnsResponses();
  }, [currentPage, fetchDnsResponses]);

  useEffect(() => {
    if (!autoRefresh) return;
    const interval = setInterval(() => {
      fetchDnsResponses();
    }, 60000);
    return () => clearInterval(interval);
  }, [autoRefresh, fetchDnsResponses]);

  const totalPages = Math.ceil(totalCount / pageSize);
  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleItemClick = (item: any) => {
    setActiveItem(activeItem === item ? null : item);
  };

  const handleExecuteAction = useCallback((action: string) => {
    console.log(`Executing: ${action}`);
    alert(`🚀 Executing: ${action}\n\nThis would trigger the remediation workflow in production.`);
  }, []);

  const handleDismissAlert = (id: string) => {
    setAlertsList((prev) => prev.filter((alert) => alert.id !== id));
  };

  const handleResetView = () => {
    setFocusedNodeId(null);
    setSelectedNode(null);
  };

  const handleClearSelection = () => {
    setSelectedNode(null);
    setFocusedNodeId(null);
  };

  const handleNodeClick = (node: NetworkNode) => {
    setSelectedNode(node);
    setFocusedNodeId(node.id);
  };

  const handleNodeSelectFromPanel = (node: NetworkNode) => {
    setSelectedNode(node);
    setFocusedNodeId(node.id);
  };

  const handleCloseDetailsPanel = () => {
    setSelectedNode(null);
    setFocusedNodeId(null);
  };

  const toggleFullscreen = () => {
    if (!graphContainerRef.current) return;
    if (!document.fullscreenElement) {
      graphContainerRef.current.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const getPageItems = () => {
    const pageItems = [];
    const maxPageItems = 5;
    const maxPageItemsDelta = Math.floor(maxPageItems / 2);
    const startPage = Math.max(currentPage - maxPageItemsDelta, 1);
    const endPage = Math.min(currentPage + maxPageItemsDelta, totalPages);
    if (startPage > 1)
      pageItems.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);
    for (let pageNumber = startPage; pageNumber <= endPage; pageNumber++) {
      pageItems.push(
        <Pagination.Item
          key={pageNumber}
          active={pageNumber === currentPage}
          onClick={() => handlePageChange(pageNumber)}
        >
          {pageNumber}
        </Pagination.Item>
      );
    }
    if (endPage < totalPages)
      pageItems.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);
    return pageItems;
  };

  const criticalCount = initialNodes.filter((n) => n.status === "critical").length;
  const warningCount = initialNodes.filter((n) => n.status === "warning").length;
  const normalCount = initialNodes.filter((n) => n.status === "normal").length;
  const threatCount = initialNodes.filter(
    (n) => n.threatLevel === "high" || n.threatLevel === "medium"
  ).length;

  // Render the appropriate view based on selected level
  const renderCentricView = () => {
    switch (currentViewLevel) {
      case "network":
        return (
          <NetworkCentricView
            nodes={filteredNodes}
            edges={edges}
            stats={stats}
            regions={regions}
          />
        );
      case "device":
        return (
          <DeviceCentricView
            nodes={filteredNodes}
            selectedNode={selectedNode}
            onNodeSelect={handleNodeClick}
            edges={edges}
          />
        );
      case "event":
        return (
          <EventCentricView
            alerts={alertsList}
            nodes={filteredNodes}
            onNodeSelect={handleNodeClick}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="dashboard-container bg-black">
      {/* Header */}
      <div className="sticky-top bg-black/95 backdrop-blur-md border-bottom border-white/10 z-1030">
        <div className="container-fluid px-4 py-3">
          <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
            <div className="d-flex align-items-center gap-3">
              <div className="p-2 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-xl">
                <Shield className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <h1 className="h5 mb-0 fw-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
                  BlackDice Retina Dashboard V2
                </h1>
                <p className="text-white/50 small mb-0">
                  Next-Gen Network Intelligence Platform
                </p>
              </div>
            </div>
            <div className="d-flex align-items-center gap-3">
              <LevelSelector
                currentLevel={currentViewLevel}
                onLevelChange={setCurrentViewLevel}
              />
              <div className="d-flex gap-2">
                <Badge bg="dark" className="px-3 py-2 bg-white/10 text-white border-white/20">
                  <Eye className="w-3 h-3 me-1" />
                  NOC Analyst
                </Badge>
                <Badge bg="dark" className="px-3 py-2 bg-white/10 text-white border-white/20">
                  <Layers className="w-3 h-3 me-1" />
                  {currentViewLevel.charAt(0).toUpperCase() + currentViewLevel.slice(1)} View
                </Badge>
              </div>
              <button
                onClick={() => setAutoRefresh(!autoRefresh)}
                className={`btn btn-sm ${autoRefresh ? "btn-primary" : "btn-outline-light"
                  }`}
              >
                <RefreshCw
                  className={`w-3 h-3 me-1 ${autoRefresh ? "animate-spin-slow" : ""}`}
                />
                Auto {autoRefresh ? "ON" : "OFF"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="container-fluid px-4 py-4">
        {/* KPI Cards Row 1 - Only show in Network view */}
        {currentViewLevel === "network" && (
          <>
            <Row className="g-3 mb-4">
              <Col xs={3} sm={6} lg={3}>
                <KpiCard
                  title="SUBSCRIBERS AFFECTED"
                  value={stats.subscribersAffected.toLocaleString()}
                  icon={<Users className="w-5 h-5" />}
                  subtitle="Total impacted customers"
                  trend="up"
                />
              </Col>
              <Col xs={3} sm={6} lg={3}>
                <KpiCard
                  title="DEVICES"
                  value={stats.devices.toLocaleString()}
                  icon={<Smartphone className="w-5 h-5" />}
                  subtitle="Active network devices"
                />
              </Col>
              <Col xs={3} sm={6} lg={3}>
                <KpiCard
                  title="HOURLY LOSS"
                  value={`$${stats.hourlyLoss.toLocaleString()}/hr`}
                  icon={<Droplet className="w-5 h-5" />}
                  subtitle="Revenue impact"
                  trend="up"
                />
              </Col>
              <Col xs={3} sm={6} lg={3}>
                <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-4 h-100">
                  <div className="d-flex gap-2 flex-wrap">
                    <Badge bg="dark" className="px-3 py-2 bg-white/10 text-white">
                      <Wifi className="w-3 h-3 me-1" />
                      Broadband
                    </Badge>
                    <Badge bg="dark" className="px-3 py-2 bg-white/10 text-white">
                      <Radio className="w-3 h-3 me-1" />
                      5G Mobile
                    </Badge>
                    <Badge bg="dark" className="px-3 py-2 bg-white/10 text-white">
                      <Phone className="w-3 h-3 me-1" />
                      VoIP
                    </Badge>
                  </div>
                  <div className="mt-2">
                    <small className="text-white/60">
                      Network Uptime: {stats.networkUptime}%
                    </small>
                    <div className="progress mt-1" style={{ height: "4px", backgroundColor: "rgba(255,255,255,0.1)" }}>
                      <div
                        className="progress-bar bg-success"
                        style={{ width: `${stats.networkUptime}%` }}
                      ></div>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>

            {/* KPI Cards Row 2 */}
            <Row className="g-3 mb-4">
              <Col xs={6} md={3}>
                <KpiCard
                  title="AVG LATENCY"
                  value={`${stats.avgLatency}ms`}
                  delta={(stats.northLatency / stats.avgLatency) * 100 - 100}
                  icon={<Globe className="w-5 h-5" />}
                  subtitle="North: 340ms (critical)"
                />
              </Col>
              <Col xs={6} md={3}>
                <KpiCard
                  title="THROUGHPUT"
                  value={`${stats.throughput}Mbps`}
                  delta={stats.throughputDelta}
                  icon={<Activity className="w-5 h-5" />}
                  subtitle={`Total: ${stats.totalBandwidth}Mbps`}
                  trend="down"
                />
              </Col>
              <Col xs={6} md={3}>
                <KpiCard
                  title="ACTIVE THREATS"
                  value={stats.activeThreats}
                  delta={stats.threatsDelta}
                  icon={<Zap className="w-5 h-5" />}
                  subtitle="Botnet nodes detected"
                  trend="up"
                />
              </Col>
              <Col xs={6} md={3}>
                <KpiCard
                  title="PACKET LOSS"
                  value={`${stats.packetLoss}%`}
                  delta={stats.packetLossDelta}
                  icon={<Percent className="w-5 h-5" />}
                  subtitle="Above threshold"
                  trend="up"
                />
              </Col>
            </Row>
          </>
        )}

        {/* Main Content - Dynamic based on view level */}
        {currentViewLevel === "network" ? (
          <>
            <Row className="g-3 mb-4">
              <Col lg={12}>
                <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
                  <div className="p-3 border-bottom border-white/10 d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <h2 className="h6 mb-0 text-white d-flex align-items-center gap-2">
                      <Boxes className="w-4 h-4" />
                      NETWORK TOPOLOGY
                      <Badge bg="primary" className="ms-2">
                        SMOOTH ZOOM & PAN
                      </Badge>
                    </h2>
                    <div className="d-flex gap-2">
                      <select
                        className="form-select form-select-sm bg-black border-white/20 text-white"
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        style={{ width: "auto" }}
                      >
                        <option value="all">All Status</option>
                        <option value="critical">Critical</option>
                        <option value="warning">Warning</option>
                        <option value="normal">Normal</option>
                      </select>
                      <div className="input-group input-group-sm" style={{ width: "160px", flexWrap: "nowrap" }}>
                        <span className="input-group-text bg-black border-white/20 text-white">
                          <Search className="w-3 h-3" />
                        </span>
                        <input
                          type="text"
                          className="form-control bg-black border-white/20 text-white"
                          placeholder="Search nodes..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                      <button
                        onClick={handleResetView}
                        className="btn btn-sm btn-outline-light"
                        title="Reset View"
                      >
                        <RefreshCw className="w-3 h-3" />
                      </button>
                      <button
                        onClick={toggleFullscreen}
                        className="btn btn-sm btn-outline-light"
                        title="Fullscreen"
                      >
                        {isFullscreen ? (
                          <Minimize2 className="w-3 h-3" />
                        ) : (
                          <Maximize2 className="w-3 h-3" />
                        )}
                      </button>
                    </div>
                  </div>
                  <div
                    ref={graphContainerRef}
                    className="position-relative"
                    style={{ height: "650px", overflow: "hidden" }}
                  >
                    <ProfessionalZoomableGraph
                      nodes={filteredNodes}
                      edges={edges}
                      onNodeClick={handleNodeClick}
                      onNodeHover={(node) => setHoveredNode(node)}
                      selectedNodeId={selectedNode?.id || null}
                      hoveredNodeId={hoveredNode?.id || null}
                      focusedNodeId={focusedNodeId}
                    />
                    {isLoading && (
                      <div className="position-absolute top-0 start-0 w-100 h-100 bg-black/50 d-flex align-items-center justify-content-center">
                        <Spinner animation="border" variant="light" />
                      </div>
                    )}
                  </div>
                </div>
              </Col>
            </Row>

            {/* Node Details Panel - Shows below topology when a node is selected */}
            {selectedNode && (
              <Row className="g-3 mb-4">
                <Col lg={12}>
                  <NodeDetailsPanel
                    selectedNode={selectedNode}
                    connectedDevices={connectedDevices}
                    onNodeSelect={handleNodeSelectFromPanel}
                    onClose={handleCloseDetailsPanel}
                  />
                </Col>
              </Row>
            )}

            <Row className="g-3 mb-4">
              <Col lg={12}>
                <div className="d-flex flex-column gap-3">
                  {/* Contextual Actions */}
                  <div className="bg-white/5 rounded-xl border border-white/10">
                    <div className="p-3 border-bottom border-white/10">
                      <h2 className="h6 mb-0 text-white d-flex align-items-center gap-2">
                        <Target className="w-4 h-4" />
                        CONTEXTUAL ACTIONS
                      </h2>
                    </div>
                    <div className="p-3 d-flex gap-3">
                      <div
                        className={`p-3 flex-grow-1 rounded-lg border transition-all cursor-pointer ${hoveredAction === "scrubbing"
                            ? "bg-blue-500/10 border-blue-400/50"
                            : "bg-white/5 border-white/10"
                          }`}
                        onMouseEnter={() => setHoveredAction("scrubbing")}
                        onMouseLeave={() => setHoveredAction(null)}
                      >
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="fw-medium text-white">
                            Activate Traffic Scrubbing
                          </span>
                          <Badge bg="danger" className="px-2 py-1">
                            HIGH
                          </Badge>
                        </div>
                        <div className="d-flex align-items-center gap-2 small text-white/60 mb-3">
                          <Shield className="w-4 h-4" />
                          <span>Effectiveness: 97%</span>
                        </div>
                        <div className="d-flex gap-2">
                          <button
                            onClick={() => handleExecuteAction("Activate Traffic Scrubbing")}
                            className="flex-grow-1 px-3 py-2 bg-blue-600 hover:bg-blue-500 rounded-lg small fw-medium transition-colors border-0 text-white"
                          >
                            <Play className="w-3 h-3 d-inline me-1" /> Execute
                          </button>
                        </div>
                      </div>
                      <div className="p-3 flex-grow-1 rounded-lg border border-white/10 bg-white/5">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="fw-medium text-white">
                            Block C&C IP 185.220.101.47
                          </span>
                          <Badge bg="danger" className="px-2 py-1">
                            HIGH
                          </Badge>
                        </div>
                        <button
                          onClick={() => handleExecuteAction("Block C&C IP")}
                          className="w-100 mt-2 px-3 py-2 bg-red-600/80 hover:bg-red-600 rounded-lg small fw-medium transition-colors border-0 text-white"
                        >
                          Block IP
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Active Alerts */}
                  <div className="bg-white/5 rounded-xl border border-white/10">
                    <div className="p-3 border-bottom border-white/10 d-flex justify-content-between align-items-center">
                      <h2 className="h6 mb-0 text-white d-flex align-items-center gap-2">
                        <AlertOctagon className="w-4 h-4" />
                        ACTIVE ALERTS ({alertsList.length})
                      </h2>
                      <button
                        onClick={() => setShowAlerts(!showAlerts)}
                        className="btn btn-sm btn-link text-white/60 p-0"
                      >
                        {showAlerts ? (
                          <Eye className="w-3 h-3" />
                        ) : (
                          <EyeOff className="w-3 h-3" />
                        )}
                      </button>
                    </div>
                    {showAlerts && (
                      <div className="p-3" style={{ maxHeight: "300px", overflowY: "auto" }}>
                        {alertsList.length === 0 ? (
                          <p className="text-white/50 small text-center mb-0">
                            No active alerts
                          </p>
                        ) : (
                          <div className="d-flex flex-column gap-2">
                            {alertsList.map((alert) => (
                              <AlertItemComponent
                                key={alert.id}
                                alert={alert}
                                onDismiss={handleDismissAlert}
                              />
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
          </>
        ) : (
          <div className="mb-4">
            {renderCentricView()}
          </div>
        )}

        {/* Regional Network Health Table - Only in Network view */}
        {currentViewLevel === "network" && (
          <Row className="g-3 mb-4">
            <Col xs={12}>
              <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
                <div className="p-3 border-bottom border-white/10">
                  <h2 className="h6 mb-0 text-white d-flex align-items-center gap-2">
                    <MapPin className="w-4 h-4" />
                    REGIONAL NETWORK HEALTH
                  </h2>
                </div>
                <div className="table-responsive">
                  <Table className="mb-0 table-hover text-white" variant="dark">
                    <thead className="bg-white/10">
                      <tr>
                        <th className="text-white">Region</th>
                        <th className="text-white">Status</th>
                        <th className="text-white">Latency (ms)</th>
                        <th className="text-white">Jitter (ms)</th>
                        <th className="text-white">Packet Loss (%)</th>
                        <th className="text-white">Peak Latency (ms)</th>
                        <th className="text-white">Subscribers</th>
                        <th className="text-white">Bandwidth</th>
                      </tr>
                    </thead>
                    <tbody>
                      {regions.map((region, idx) => (
                        <tr
                          key={idx}
                          className={
                            region.status === "critical"
                              ? "bg-red-500/10"
                              : region.status === "warning"
                                ? "bg-amber-500/10"
                                : ""
                          }
                        >
                          <td className="fw-medium text-white">{region.name}</td>
                          <td>
                            <Badge
                              bg={
                                region.status === "critical"
                                  ? "danger"
                                  : region.status === "warning"
                                    ? "warning"
                                    : "success"
                              }
                            >
                              {region.status.toUpperCase()}
                            </Badge>
                          </td>
                          <td
                            className={region.latency > 100 ? "text-danger" : "text-white"}
                          >
                            {region.latency}ms
                          </td>
                          <td className="text-white">{region.jitter}ms</td>
                          <td
                            className={
                              region.packetLoss > 1 ? "text-danger" : "text-white"
                            }
                          >
                            {region.packetLoss}%
                           </td>
                          <td className="text-white">{region.peakLatency}ms</td>
                          <td className="text-white">
                            {region.subscribers?.toLocaleString() || "N/A"}
                          </td>
                          <td className="text-white">{region.bandwidth || "N/A"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              </div>
            </Col>
          </Row>
        )}

        {/* DNS Filter Tests Table - Common across all views */}
        <Row className="g-3">
          <Col xs={12}>
            <div className="bg-white/5 rounded-xl border border-white/10 overflow-hidden">
              <div className="p-3 border-bottom border-white/10 d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h2 className="h6 mb-0 text-white d-flex align-items-center gap-2">
                  <Database className="w-4 h-4" />
                  FILTER TESTS FAILED
                </h2>
                <div className="d-flex align-items-center gap-2">
                  <Badge bg="secondary" className="bg-white/20 text-white">
                    Total: {totalCount}
                  </Badge>
                  {isLoading && <Spinner animation="border" size="sm" variant="light" />}
                </div>
              </div>
              <div className="table-responsive">
                <Table className="mb-0 text-white" variant="dark">
                  <thead className="bg-white/10">
                    <tr>
                      <th className="text-white">Date</th>
                      <th className="text-white">Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dnsResponses.length === 0 ? (
                      <tr>
                        <td colSpan={2} className="text-center text-white/50 py-4">
                          No data available
                        </td>
                      </tr>
                    ) : (
                      dnsResponses.map((item: any) => (
                        <React.Fragment key={item.uuid}>
                          <tr
                            className={!item.result ? "bg-red-500/10 cursor-pointer" : "cursor-pointer"}
                            onClick={() => handleItemClick(item)}
                            style={{ cursor: "pointer" }}
                          >
                            <td className="text-white">
                              {formatDateTime(item.responses[0]?.createdAt)}
                            </td>
                            <td>
                              <Badge bg={item.result ? "success" : "danger"}>
                                {item.result ? "Success" : "Fail"}
                              </Badge>
                            </td>
                          </tr>
                          {activeItem === item && (
                            <tr>
                              <td colSpan={2} className="p-0">
                                <div className="p-3 bg-white/5">
                                  <Table bordered size="sm" className="mb-0 text-white" variant="dark">
                                    <thead>
                                      <tr>
                                        <th className="text-white">Description</th>
                                        <th className="text-white">Fqdn</th>
                                        <th className="text-white">Should be</th>
                                        <th className="text-white">Result</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {item.responses.map((response: any) => (
                                        <tr
                                          key={response.dnsQueryId}
                                          className={!response.result ? "bg-red-500/10" : ""}
                                        >
                                          <td className="text-white">{response.description}</td>
                                          <td className="text-white">{response.fqdn}</td>
                                          <td className="text-white">
                                            {response.shouldBe ? "Allowed" : "Blocked"}
                                          </td>
                                          <td>
                                            <Badge bg={response.result ? "success" : "danger"}>
                                              {response.result ? "Success" : "Fail"}
                                            </Badge>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </Table>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))
                    )}
                  </tbody>
                </Table>
              </div>
              {dnsResponses.length > 0 && (
                <div className="p-3 border-top border-white/10 d-flex justify-content-center">
                  <Pagination className="mb-0">
                    <Pagination.First
                      onClick={() => handlePageChange(1)}
                      disabled={currentPage === 1}
                    />
                    <Pagination.Prev
                      onClick={() => handlePageChange(currentPage - 1)}
                      disabled={currentPage === 1}
                    />
                    {getPageItems()}
                    <Pagination.Next
                      onClick={() => handlePageChange(currentPage + 1)}
                      disabled={currentPage === totalPages}
                    />
                    <Pagination.Last
                      onClick={() => handlePageChange(totalPages)}
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                </div>
              )}
            </div>
          </Col>
        </Row>
      </div>

      <style>{`
        .animate-spin-slow {
          animation: spin 2s linear infinite;
        }
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        .form-select,
        .form-control,
        .input-group-text {
          background-color: black !important;
          color: white !important;
        }
        .form-select option {
          background-color: black !important;
          color: white !important;
        }
        .page-link {
          background-color: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.2);
          color: white;
        }
        .page-link:hover {
          background-color: rgba(255, 255, 255, 0.2);
          color: white;
        }
        .page-item.active .page-link {
          background-color: #0d6efd;
          border-color: #0d6efd;
        }
        .progress {
          background-color: rgba(255, 255, 255, 0.1);
        }
        .cursor-pointer {
          cursor: pointer;
        }
        .bg-gradient-to-br {
          background: linear-gradient(135deg, var(--tw-gradient-from) 0%, var(--tw-gradient-to) 100%);
        }
        .bg-gradient-to-r {
          background: linear-gradient(90deg, var(--tw-gradient-from) 0%, var(--tw-gradient-to) 100%);
        }
        .bg-clip-text {
          -webkit-background-clip: text;
          background-clip: text;
        }
        .text-transparent {
          color: transparent;
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }
        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
        .max-h-64 {
          max-height: 16rem;
        }
        .space-y-2 > * + * {
          margin-top: 0.5rem;
        }
      `}</style>
    </div>
  );
};

// Helper function for date formatting
function formatDateTime(dateString: string | null): string {
  if (!dateString) return "N/A";
  const date = new Date(dateString);
  return date.toLocaleString();
}

// Phone icon component
const Phone = (props: any) => (
  <svg
    {...props}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z" />
  </svg>
);

export default DashboardV2;